import React, { useState, useEffect, useRef, useMemo, startTransition } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import useFilterPersistence from "../../../hooks/useFilterPersistence";

import { makeStyles } from "@material-ui/core/styles";
import { dataService } from "../../../Services/data/data.service";
import { getMeasurementQty } from "../../Shared/PriceBreakdown";
import { parseDateToDDMMYYTime, parseNewDateToDDMMYYYnoTime, parseNewDateToDDMMYYYWithoutSeconds } from "../../../Utils/dateHelper";
import {
  valueTypeCodes,
  controlTypes,
  externalApiTypes,
  ProjectFilterTypes,
  resultColumns,
  profitLossResultColumns,
  graphTypes,
  timeLineVisualisationTypes,
  registrationPointColors,
  numericFilterOperations,
} from "../../../Config/constants";

// Packages
import { Spin } from "react-cssfx-loading";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";

// Components
import CompactDateRangePicker from "../../Shared/Buttons/CompactDateRangePicker";
import TimeRangeSelector from "../../Shared/Buttons/TimeRangeSelector";
import TimeRangeScroll from "../../Shared/Buttons/TimeRangeScroll";
import DateRangeSlider from "../../Shared/DateSelection/DateRangeSlider";
import DevExDataTable from "../Results/Components/DevExDataTable";
import ProfitLossDevExDataTable from "./Components/ProfitLossDevExDataTable";
import BoolToggle from "../../Shared/Buttons/BoolToggle";
import YamazumiChart from "./Components/YamazumiChart";
import OutputChart from "./Components/OutputChart";
import TextfieldInput from "../../Shared/TextField";
import Input from "../../Shared/Input";
import TimeLineChart from "../Results/Components/TimeLineChart";
import FinancialChart from "./Components/FinancialChart";
import { Select, MenuItem, Box, Tooltip, Divider } from "@mui/material";
import { Select as CustomSelect, SelectTrigger, SelectContent, SelectItem, SelectGroup, SelectLabel } from "../../Shared/Select";
import FinancialSummaryTable from "./Components/FinancialSummaryTable";
import EventChart from "../ProjectReport/Components/EventChart";
import TableHighLow from "../ProjectReport/Components/TableHighLow";
import ProfitLossReport from "./Components/ProfitLossReport";
import GraphTabs from "./Components/GraphTabs";
import ProjectOrderBar from "./Components/ProjectOrderBar";
import Button from "../../Shared/Buttons/Button";

import InsightsTab from "./Components/InsightsTab";
import ProjectionsTab from "./Components/ProjectionsTab";
import ShareGraphDropdown from "./Components/ShareGraphDropdown";
import { MapPin, X, Plus, Minus, Timer, ScanBarcode, Image, TextCursorInput, LinkIcon, List, DiamondPlus } from "lucide-react";
import Breadcrumb from "../../Shared/Header/Breadcrumb";
import HeaderTitle from "../../Shared/Header/HeaderTitle";

export default function ModifyGraphPage(props) {
  const { graphId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loadAmount = 0;

  const viewRangeKey = `app:view-range:graph-modify-${graphId}`;

  const { extraParams, setExtraParam } = useFilterPersistence({
    pageKey: `graph-modify-${graphId}`,
    filterSchema: {},
    extraParamDefaults: {
      tab: graphTypes.INSIGHTS,
      startDate: '',
      endDate: '',
      showLinear: false,
      labelVisible: true,
      percentVis: false,
      showRecurring: false,
      timelineView: 'timeline',
      dataFilters: '[]',
      financialFilters: '[]',
      zoom: '1 Week',
      viewFrom: '',
      viewTo: '',
    },
  });

  // General
  const [create, setCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchingData, setFetchingData] = useState(false); // Are measurements being fetched?
  const [selectedGraphType, setSelectedGraphType] = useState(extraParams.tab); // Default graph type
  const [graphProjects, setGraphProjects] = useState([]);
  const [allFilters, setAllFilters] = useState();
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [activeCurrency, setActiveCurrency] = useState(null);
  const [measurementsToDisplay, setMeasurementsToDisplay] = useState([]);
  const [orderedProjectNames, setOrderedProjectNames] = useState([]);

  // Datagrid properties
  const [originalMeasurementRows, setOriginalMeasurementRows] = useState([]); // All measurementrows for current GraphDetail - should not be overwritten
  const [types, setTypes] = useState([]);

  // Sharing graph
  const [graphIsPublished, setGraphIsPublished] = useState(false); // Is this graph published?
  const [sharingLoading, setSharingLoading] = useState(false); // Loading state for publish/unpublish API call

  // Filtering
  const [timeRangeDates, setTimeRangeDates] = useState({
    fromDate: new Date(Date.now() - 86400000),
    startDate: new Date(Date.now() - 86400000),
    toDate: new Date(Date.now()),
    endDate: new Date(Date.now()),
  });
  const [noGraphFound, setNoGraphFound] = useState(false);
  const [graphConfigurationObject, setGraphConfigurationObject] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [graphConfigurationName, setGraphConfigurationName] = useState("");
  const [selectedProject, setSelectedProject] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [graphConfigurationId, setGraphConfigurationId] = useState(null);
  const [graphConfigurationUniqueId, setGraphConfigurationUniqueId] = useState(null);
  const [labelVisible, setLabelVisible] = useState(extraParams.labelVisible);

  // Timeline chart
  const [chartRows, setChartRows] = useState([]);
  const [graphDetails, setGraphDetails] = useState([]);
  const [showLinearTimeline, setShowLinearTimeline] = useState(extraParams.showLinear);
  const [timelineChartView, setTimelineChartView] = useState(extraParams.timelineView);
  const [timeRangeZoom, setTimeRangeZoom] = useState(() => {
    if (extraParams.viewFrom) return extraParams.zoom || '1 Week';
    try { const s = JSON.parse(localStorage.getItem(viewRangeKey)); return s?.zoom || '1 Week'; } catch { return '1 Week'; }
  });
  const [viewRangeDates, setViewRangeDates] = useState(() => {
    if (extraParams.viewFrom && extraParams.viewTo) {
      return { fromDate: new Date(extraParams.viewFrom), endDate: new Date(extraParams.viewTo) };
    }
    try {
      const s = JSON.parse(localStorage.getItem(viewRangeKey));
      if (s?.viewFrom && s?.viewTo) return { fromDate: new Date(s.viewFrom), endDate: new Date(s.viewTo) };
    } catch {}
    const now = new Date();
    return { fromDate: new Date(now.getTime() - 7 * 86400000), endDate: now };
  });

  // Financial chart
  const summaryRef = React.createRef();
  const highLowRef = React.createRef();
  const [financialChartData, setFinancialChartData] = useState([]);
  const [projectDetailNames, setDetailsNames] = useState({});
  const [totalPrice, setTotalPrice] = useState(0); // for summary
  const [priceData, setPriceData] = useState([]);
  const [highLowGraphProjects, setHighLowGraphProjects] = useState([]);

  // Profit/Loss report
  const profitLossReportComponentRef = useRef(null);
  const [profitLossReportData, setProfitLossReportData] = useState([]);
  const [allAccountCategories, setAllAccountCategories] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]);
  const [allPriceBundles, setAllPriceBundles] = useState([]);
  const [usedAccountCategories, setUsedAccountCategories] = useState([]);
  const [profitLossDates, setProfitLossDates] = useState({
    0: { sequence: 1, fromDate: new Date(Date.now() - 86400000), startDate: new Date(Date.now() - 86400000), toDate: new Date(Date.now()), endDate: new Date(Date.now()) },
  });
  const [recurringFinancials, setRecurringFinancials] = useState([]);
  const [showRecurringPrices, setShowRecurringPrices] = useState(extraParams.showRecurring);

  const [boolCompareDate, setBoolCompareDate] = useState(false);
  const [compareLimit, setCompareLimit] = useState(5);
  const [percentageVisible, setPercentageVisible] = useState(extraParams.percentVis);

  // Date range filters
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  //DevExData
  const [selection, setSelection] = useState([]);
  const [profitLossSelection, setProfitLossSelection] = useState([]);

  // Tab-specific filtering
  const [dataFilters, setDataFilters] = useState(() => { try { return JSON.parse(extraParams.dataFilters); } catch { return []; } });
  const [financialFilters, setFinancialFilters] = useState(() => { try { return JSON.parse(extraParams.financialFilters); } catch { return []; } });

  const [dataMeasurementRows, setDataMeasurementRows] = useState([]);
  const [financialMeasurementRows, setFinancialMeasurementRows] = useState([]);

  const yamazumiMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          row.stopTime !== null &&
          (row.typeId === controlTypes.STOPWATCH ||
            (row.typeId === controlTypes.EXTERNALAPI &&
              row.externalApiTypeId === externalApiTypes.STOPWATCH))
      ),
    [dataMeasurementRows]
  );

  const outputMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          row.typeId === controlTypes.COUNTER ||
          row.typeId === controlTypes.DATALIST ||
          row.typeId === controlTypes.FREETEXT ||
          row.typeId === controlTypes.SCANNER ||
          (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.DATA) ||
          (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.NUMERIC)
      ),
    [dataMeasurementRows]
  );

  const timelineEventMeasurementRows = useMemo(
    () =>
      dataMeasurementRows.filter(
        (row) =>
          !(row.typeId === controlTypes.STOPWATCH ||
            (row.typeId === controlTypes.EXTERNALAPI &&
              row.externalApiTypeId === externalApiTypes.STOPWATCH))
      ),
    [dataMeasurementRows]
  );

  const viewFilteredMeasurements = useMemo(() => {
    if (!viewRangeDates?.fromDate || !viewRangeDates?.endDate) return measurementsToDisplay;
    const from = viewRangeDates.fromDate.getTime();
    const to = viewRangeDates.endDate.getTime();
    return measurementsToDisplay.filter((m) => {
      let t = null;
      if (m.startTime) { const v = new Date(m.startTime).getTime(); if (!isNaN(v)) t = v; }
      if (t === null && m.insertedOn) { const v = new Date(m.insertedOn).getTime(); if (!isNaN(v)) t = v; }
      return t === null || (t >= from && t <= to);
    });
  }, [measurementsToDisplay, viewRangeDates]);

  // Possible remove
  const [filters, setFilters] = useState([]);

  // Filtering
  const [openSearch, setOpenSearch] = useState(false);
  const [graphConfigurationData, setGraphConfigurationData] = useState({
    dateRange: {
      fromDate: new Date(Date.now() - 86400000),
      toDate: new Date(Date.now()),
    },
    projects: [],
  });
  const [allUserCompanies, setAllUserCompanies] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [graphConfigurationDescription, setGraphConfigurationDescription] = useState("");

  // Form validation
  const [graphConfigurationNameError, setGraphConfigurationNameError] = useState("");
  const [selectedProjectsError, setSelectedProjectsError] = useState("");
  const [graphConfigurationCompanyId, setGraphConfigurationCompanyId] = useState(null);
  const [graphConfigurationCompanyIdError, setGraphConfigurationCompanyIdError] = useState("");

  // Projection (Financial projection)
  const [activeProjection, setActiveProjection] = useState(false);
  const [simulationActiveScenarioId, setSimulationActiveScenarioId] = useState("baseline");
  const [simulationAppliedScenarioId, setSimulationAppliedScenarioId] = useState(null);
  const [graphInsights, setGraphInsights] = useState(null);
  const [serverProfitLossData, setServerProfitLossData] = useState(null);

  useEffect(() => {
    if (graphId !== null && graphId !== "" && graphId !== "new") {
      getGraphData(graphId);
      setCreate(false);
    } else if (graphId === "new") {
      setLoading(false);
      setCreate(true);
    }
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist tab and toggle state
  useEffect(() => { setExtraParam('tab', selectedGraphType); }, [selectedGraphType]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('showLinear', showLinearTimeline); }, [showLinearTimeline]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('labelVisible', labelVisible); }, [labelVisible]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('percentVis', percentageVisible); }, [percentageVisible]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('showRecurring', showRecurringPrices); }, [showRecurringPrices]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('timelineView', timelineChartView); }, [timelineChartView]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('dataFilters', JSON.stringify(dataFilters)); }, [dataFilters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setExtraParam('financialFilters', JSON.stringify(financialFilters)); }, [financialFilters]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    // set usedAccountCategories
    let tempUsedAccountCategories = [];
    if (originalMeasurementRows !== null && originalMeasurementRows.length > 0 && allAccountCategories !== null && allAccountCategories.length > 0) {
      originalMeasurementRows.forEach((measurement) => {
        if (measurement.accountCategoryId) {
          const accountCategory = allAccountCategories.find((category) => category.id === measurement.accountCategoryId);
          if (accountCategory && !tempUsedAccountCategories.some((cat) => cat.id === accountCategory.id)) {
            tempUsedAccountCategories.push(accountCategory);
          }
        }
      });
      setUsedAccountCategories(tempUsedAccountCategories);
    } else {
      setUsedAccountCategories([]);
    }
  }, [originalMeasurementRows, allAccountCategories]);

  useEffect(() => {
    // set graphConfigurationData.dateRange to timeRangeDates but only to and from
    let tempGraphConfigurationData = JSON.parse(JSON.stringify(graphConfigurationData));
    tempGraphConfigurationData.dateRange = {
      fromDate: timeRangeDates.fromDate,
      toDate: timeRangeDates.toDate,
    };
    setGraphConfigurationData(tempGraphConfigurationData);
  }, [timeRangeDates]);

  // Price bundles power the per-row breakdown tooltip on all result tables and
  // the simulations P&L calculation. The graph-scoped endpoint returns bundles
  // already grouped by company with effectiveAccountId on each item, which is
  // required by computePricePL in ProjectionsTab and calculateProjection here.
  useEffect(() => {
    if (!graphId || graphId === "new") return;
    dataService
      .getAllPriceBundlesForGraphConfigurationByUniqueId(graphId)
      .then((result) => setAllPriceBundles(result || []))
      .catch(() => {});
  }, [graphId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!graphId || !timeRangeDates?.fromDate || !timeRangeDates?.toDate) return;
    let cancelled = false;
    dataService
      .getGraphInsights(graphId, timeRangeDates.fromDate.toISOString(), timeRangeDates.toDate.toISOString())
      .then((result) => {
        if (!cancelled && result) setGraphInsights(result);
      });
    return () => { cancelled = true; };
  }, [graphId, timeRangeDates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading || !graphId || !timeRangeDates?.fromDate || !timeRangeDates?.toDate) return;
    let cancelled = false;
    dataService
      .getGraphProfitLoss(
        graphId,
        timeRangeDates.fromDate.toISOString(),
        timeRangeDates.toDate.toISOString()
      )
      .then((result) => {
        if (!cancelled && result) startTransition(() => setServerProfitLossData(result));
      });
    return () => { cancelled = true; };
  }, [loading, graphId, timeRangeDates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let sortedCompanies = null;

    // Fetch companies immediately so the dropdown populates without waiting for the slower projects fetch
    const companiesPromise = dataService.getAllCompaniesByUserId(props.Login.userid)
      .then((results) => {
        if (results != null && results.length > 0) {
          if (results.length === 1) setGraphConfigurationCompanyId(results[0].id);
          sortedCompanies = results.sort((a, b) => a.name.localeCompare(b.name));
          setAllUserCompanies(sortedCompanies);
        }
      })
      .catch(() => {});

    // Fetch types and projects in parallel; group projects under companies once both are ready
    Promise.all([
      dataService.getTypes(),
      dataService.getAllUserCompanyProjects(props.Login.userid, 0, loadAmount),
      companiesPromise,
    ])
      .then(([typeResults, projectResults]) => {
        if (typeResults != null && typeResults.length > 0) {
          setTypes(typeResults.sort((a, b) => a.description.localeCompare(b.description)));
        }
        if (projectResults != null && projectResults.length > 0) {
          if (sortedCompanies != null) {
            const grouped = JSON.parse(JSON.stringify(sortedCompanies));
            grouped.forEach((cr) => {
              cr.projects = projectResults.filter((x) => x.companyId === cr.id).sort((a, b) => a.projectName.localeCompare(b.projectName));
            });
            setAllProjects(grouped);
          } else {
            setAllProjects(projectResults);
          }
        }
      })
      .catch(() => {});
  }, [props.Login.userid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // GetAllCurrencies
    dataService
      .getAllCurrencies()
      .then((results) => {
        setAllCurrencies(results);
      })
      .catch(() => {
        setAllCurrencies([]);
      });
  }, []);

  useEffect(() => {
    // set active currency
    if (selectedProjects.length > 0 && allCurrencies.length > 0) {
      let currency = allCurrencies.find((x) => x.id === selectedProjects[0].currencyId);
      setActiveCurrency(currency);
    }
  }, [selectedProjects, allCurrencies]);

  // Re-format P&L report when recurring prices toggle or data changes
  useEffect(() => {
    if (!loading && profitLossDates) {
      let measurements = financialMeasurementRows.filter(x => x.checked);
      formatProfitLossReportData(measurements, profitLossDates).then((formattedData) => {
        setProfitLossReportData(formattedData);
      });
    }
  }, [showRecurringPrices, recurringFinancials]); // eslint-disable-line react-hooks/exhaustive-deps

  // useEffect(() => {
  //     onFiltersChange();
  // }, [originalMeasurementRows])

  const roundToSecond = (date) => {
    // remove milliseconds
    return new Date(date.setMilliseconds(0));
  };

  // Convert seconds to hh:mm:ss
  function convertHMS(value) {
    const sec = parseInt(value, 10); // convert value to number if it's string
    let hours = Math.floor(sec / 3600); // get hours
    let minutes = Math.floor((sec - hours * 3600) / 60); // get minutes
    let seconds = sec - hours * 3600 - minutes * 60; //  get seconds
    // add 0 if value < 10; Example: 2 => 02
    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    return hours + ":" + minutes + ":" + seconds; // Return is HH : MM : SS
  }

  // Event duration in minutes
  const getDurationInMinutes = (measurement) => {
    if (measurement.startTime && measurement.stopValue) {
      const start = new Date(measurement.startTime);
      const stop = new Date(measurement.stopValue);
      const durationInMs = stop - start; // Duration in milliseconds
      return Math.floor(durationInMs / 60000); // Convert to minutes and round down
    }
    return null; // Return null if startTime or stopValue is not available
  };

  const getStartHour = (startTime) => {
    if (startTime !== null && startTime !== undefined) {
      const date = new Date(startTime);
      return date.getHours();
    } else {
      return null;
    }
  };

  const getStartDayOfWeek = (startTime) => {
    if (startTime !== null && startTime !== undefined) {
      const date = new Date(startTime);
      const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      return days[date.getDay()];
    } else {
      return null;
    }
  };

  const handleGraphTypeSelection = (type) => {
    setSelectedGraphType(type);
  };

  // Setting properties for current graphConfiguration
  const getGraphData = (graphId) => {
    var graphdataResults = [];
    let tempGraphData = [];
    var projectMeasurements = [];
    let tempTimeRangeDates = {};
    let graphDetails = [];

    let tempDataMeasurementRows = [];
    let tempFinancialMeasurementRows = [];

    let tempSelectedProjects = JSON.parse(JSON.stringify(selectedProject));

    dataService
      .getGraphConfigurationByUniqueId(graphId)
      .then(async (results) => {
        setGraphConfigurationName(results.name);
        setGraphConfigurationId(results.id);
        setGraphConfigurationObject(results);
        setGraphConfigurationUniqueId(results.uniqueId);

        setGraphConfigurationDescription(results.description);
        setGraphConfigurationCompanyId(results.companyId);
        const parsedMeta = JSON.parse(results.metaData);
        setGraphConfigurationData(parsedMeta);
        setGraphIsPublished(results.isPublic);
        // handleGraphConfiguration(JSON.parse(results.metaData));

        graphdataResults = results;
        if (graphdataResults !== null && graphdataResults !== undefined) {
          setGraphProjects(graphdataResults.projects);
          const sortedBySequence = [...graphdataResults.projects].sort((a, b) => {
            const seqA = parsedMeta?.projects?.find((sp) => sp.projectId === a.id)?.sequence ?? 999;
            const seqB = parsedMeta?.projects?.find((sp) => sp.projectId === b.id)?.sequence ?? 999;
            return seqA - seqB;
          });
          setOrderedProjectNames(sortedBySequence.map((p) => p.resourceDescription));
          graphdataResults.projects.forEach((project, index) => {
            tempSelectedProjects.push(project);
            // tempGraphData.push(parseProjectData(project).flat());
            project.details.forEach((detail) => {
              detail.resourceName = project.resourceDescription;
              graphDetails.push(detail);
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                detail.measurements.forEach((measurement) => {
                  measurement.registrationPointColor = registrationPointColors[index];
                  measurement.isProfit = detail.isProfit;
                });

                projectMeasurements.push(detail.measurements);
              }
            });
            setGraphDetails(graphDetails);
          });

          projectMeasurements = projectMeasurements.flat();
          let tempProjectMeasurementRows;
          tempProjectMeasurementRows = projectMeasurements.map((y) => ({
            accountId: y.accountId,
            accountName: y.accountName,
            accountCategoryId: y.accountCategoryId,
            accountCategoryName: y.accountCategoryName,
            checked: true,
            projectId: y.projectId,
            toDate: y.toDate,
            fromDate: y.fromDate,
            color: y.color,
            id: y.id,
            typeId: y.typeId,
            valueTypeId: y.valueTypeId,
            companyId: y.companyId,
            qrCode: y.qrCode,
            companyName: y.companyName,
            detailId: y.projectDetailId,
            projectName: y.projectName,
            resourceName: y.resourceName,
            registrationPointColor: y.registrationPointColor,
            description: y.description,
            durationInMinutes: getDurationInMinutes(y),
            startTime: y.startTime,
            startHour: getStartHour(y.startTime),
            startDayOfWeek: getStartDayOfWeek(y.startTime),
            stopTime: y.stopValue,
            insertedOn: new Date(y.insertedOn),
            updatedOn: y.stopTime,
            measurementSessionId: y.measurementSessionId,
            measurementValue: getMeasurementValue(
              y.typeId,
              y.externalApiTypeId,
              y.textValue,
              y.integerValue,
              y.dateValue,
              y.dataListValue,
              y.imageLink,
              y.externalApiValues,
              y.isNegative,
              y.numericValue
            ),
            integerValue: y.integerValue,
            running: y.running,
            thumbnail: y.thumbnail,
            imageId: y.imageId,
            commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
            commentImage: y.commentImage,
            commentId: y.commentId,
            valueType: getValueType(y.valueTypeId),
            keyValue: y.keyValue,
            eventPrice: y.eventPrice || 0,
            signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
            priceBundleId: y.priceBundleId,
            externalApiTypeId: y.externalApiTypeId,
            isProfit: y.isProfit,
            isNegative: y.isNegative,
            numericValue: y.numericValue,
            sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
            sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
          }));
          tempDataMeasurementRows.push(...tempProjectMeasurementRows.flat());

          tempFinancialMeasurementRows = tempDataMeasurementRows.filter((row) => row.eventPrice > 0);

          // Setting timerangedates
          let graphConfigurationMetaData = JSON.parse(results.metaData);
          if (graphConfigurationMetaData.dateRange !== null && graphConfigurationMetaData.dateRange !== undefined) {
            let dateRange = JSON.parse(results.metaData).dateRange;
            tempTimeRangeDates = { fromDate: new Date(dateRange.fromDate), startDate: new Date(dateRange.fromDate), toDate: new Date(dateRange.toDate), endDate: new Date(dateRange.toDate) };
          } else {
            let element = getLatestStartTimeElement(tempDataMeasurementRows);
            let from = timeRangeDates.fromDate;
            let to = timeRangeDates.toDate;
            if (element !== null && element !== undefined) {
              to = new Date(element.startTime);
              from = new Date(element.startTime);
              from.setHours(from.getHours() - 24);
              const formattedDate = from.toString();
              from = formattedDate;
              tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to };
            } else {
              tempTimeRangeDates = timeRangeDates;
            }
          }
          // Restore user-selected date range if previously changed
          if (extraParams.startDate && extraParams.endDate) {
            tempTimeRangeDates = {
              fromDate: new Date(extraParams.startDate),
              startDate: new Date(extraParams.startDate),
              toDate: new Date(extraParams.endDate),
              endDate: new Date(extraParams.endDate),
            };
          }

          graphdataResults.projects.forEach((project) => {
            // tempSelectedProjects.push(project)
            tempGraphData.push(parseProjectData(project));
            project.details.forEach((detail) => {
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                projectMeasurements.push(detail.measurements);
              }
            });
          });

          const tempRangeFilteredRows = (tempTimeRangeDates?.startDate && tempTimeRangeDates?.endDate)
            ? tempDataMeasurementRows.filter((row) => {
                const t = roundToSecond(new Date(row.insertedOn));
                return !(t > new Date(tempTimeRangeDates.endDate) || t <= new Date(tempTimeRangeDates.startDate));
              })
            : tempDataMeasurementRows;

          const earlyInsightsResult = await dataService.getGraphInsights(
            graphId,
            tempTimeRangeDates.fromDate.toISOString(),
            tempTimeRangeDates.toDate.toISOString()
          );
          if (earlyInsightsResult) setGraphInsights(earlyInsightsResult);

          // Setting the states for measurements and graph data
          const selectionPromise = new Promise((resolve) => {
            const indexes = tempDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            const profitLossIndexes = tempFinancialMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setSelection(indexes);
            setProfitLossSelection(profitLossIndexes);
            resolve();
          });

          const measurementRowsPromise = new Promise((resolve) => {
            setDataMeasurementRows([...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            setFinancialMeasurementRows(
              [...tempDataMeasurementRows]
                .flat()
                .sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn))
                .filter((row) => row.eventPrice > 0)
            );
            resolve();
          });

          const originalMeasurementRowsPromise = new Promise((resolve) => {
            const sortedRows = [...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn));
            setOriginalMeasurementRows(sortedRows);
            resetViewRangeToDefault(sortedRows);
            resolve();
          });

          // Data for yamazumi
          const graphDataPromise = new Promise((resolve) => {
            setGraphData(tempGraphData.flat());
            resolve();
          });

          // Measurements for display
          const measurementsToDisplayPromise = new Promise((resolve) => {
            setMeasurementsToDisplay(tempRangeFilteredRows.sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            resolve();
          });

          // Data for timeline view
          const chartRowsPromise = new Promise((resolve) => {
            let data = chartData(tempDataMeasurementRows.flat());
            setChartRows(data);
            resolve();
          });

          // Data for financial view
          const financialDataPromise = new Promise((resolve) => {
            constructPriceData(graphdataResults.projects);

            let financialChartMeasurements = tempDataMeasurementRows.sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn));

            let data = constructFinancialData(graphDetails, financialChartMeasurements);
            let detailNames = constructDetailCollection(graphDetails);

            setDetailsNames(detailNames);
            setFinancialChartData(data);

            // setting data for highlow table
            let tempGraphProjects = [...graphdataResults.projects]; // Clone the projects array

            tempGraphProjects = tempGraphProjects.map((project) => {
              return {
                ...project,
                details: project.details.map((detail) => {
                  return {
                    ...detail,
                    measurements: detail.measurements,
                  };
                }),
              };
            });
            setHighLowGraphProjects(tempGraphProjects);

            resolve();
          });

          var dateranges = { 0: tempTimeRangeDates };
          // Data for profit loss report
          const profitLossReportDataPromise = new Promise((resolve) => {
            let tempProfitLossReportData;
            formatProfitLossReportData(tempProjectMeasurementRows, dateranges).then((formattedData) => {
              tempProfitLossReportData = formattedData;
              setProfitLossReportData(tempProfitLossReportData);
            });

            resolve();
          });

          const timeRangeDatesPromise = new Promise((resolve) => {
            setTimeRangeDates(tempTimeRangeDates);

            // add sequence to tempTimeRangeDates
            let tempProfitLossDates = tempTimeRangeDates;
            tempProfitLossDates.sequence = 1;
            setProfitLossDates({ 0: tempProfitLossDates });
            resolve();
          });

          const selectedProjectsPromise = new Promise((resolve) => {
            setSelectedProjects(tempSelectedProjects);
            resolve();
          });

          // Wait for all state updates to complete before setting loading to false
          Promise.all([
            selectionPromise,
            measurementRowsPromise,
            originalMeasurementRowsPromise,
            graphDataPromise,
            measurementsToDisplayPromise,
            chartRowsPromise,
            financialDataPromise,
            profitLossReportDataPromise,
            timeRangeDatesPromise,
            selectedProjectsPromise,
          ]).then(() => {
            setLoading(false); // Only set loading to false after all updates
            
            // Fetch recurring financials after graph data is loaded
            fetchRecurringFinancials(tempDataMeasurementRows, tempTimeRangeDates);
          });
        }
      })
      .catch(() => {
        setNoGraphFound(true);
        toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch recurring financials for all companies in the graph
  const fetchRecurringFinancials = async (measurementRows, timeRangeDates) => {
    try {
      const companyIds = [...new Set(measurementRows.map(row => row.companyId))];
      
      if (companyIds.length === 0 || !timeRangeDates.fromDate || !timeRangeDates.toDate) {
        setRecurringFinancials([]);
        return;
      }

      const result = await dataService.getRecurringFinancialsByCompanyIdsAndDateRange(
        companyIds,
        timeRangeDates.fromDate.toISOString(),
        timeRangeDates.toDate.toISOString()
      );

      setRecurringFinancials(result || []);
    } catch (error) {
      console.error("Error fetching recurring financials:", error);
      setRecurringFinancials([]);
    }
  };

  const fetchConfigurationByDateRange = (dateRange) => {
    var graphdataResults = [];
    let tempGraphData = [];
    var projectMeasurements = [];
    let tempTimeRangeDates = {};
    let graphDetails = [];

    let tempDataMeasurementRows = [];
    let tempFinancialMeasurementRows = [];

    let tempSelectedProjects = JSON.parse(JSON.stringify(selectedProjects)); // Use selectedProjects instead of selectedProject

    setFetchingData(true);
    dataService
      .getGraphConfigurationByUniqueIdAndDateRange(graphId, dateRange.fromDate.toISOString(), dateRange.toDate.toISOString())
      .then(async (results) => {
        setGraphConfigurationObject(results);

        graphdataResults = results;
        if (graphdataResults !== null && graphdataResults !== undefined) {
          // Process saved configuration projects
          graphdataResults.projects.forEach((project, index) => {
            project.details.forEach((detail) => {
              detail.resourceName = project.resourceDescription;
              graphDetails.push(detail);
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                detail.measurements.forEach((measurement) => {
                  measurement.registrationPointColor = registrationPointColors[index];
                  measurement.isProfit = detail.isProfit;
                  measurement.resourceName = project.resourceDescription;
                });

                projectMeasurements.push(detail.measurements);
              }
            });
          });

          // Find projects that are selected but not in the saved configuration
          const savedProjectIds = graphdataResults.projects.map((p) => p.id);
          const unsavedProjects = tempSelectedProjects.filter((p) => !savedProjectIds.includes(p.id));

          // Fetch unsaved projects for the new date range
          const unsavedProjectPromises = unsavedProjects.map(async (project) => {
            try {
              const projectData = await dataService.getAllProjectGraphMeasurementsByIdAndDateRange(project.id, dateRange.fromDate.toISOString(), dateRange.toDate.toISOString());

              if (projectData && projectData.details) {
                // Find the next index for registration point color
                const nextIndex = graphdataResults.projects.length + unsavedProjects.indexOf(project);

                projectData.details.forEach((detail) => {
                  detail.resourceName = project.resourceDescription;
                  detail.externalApiTypeId = projectData.details.find((x) => x.id === detail.id)?.externalApiTypeId || null;
                  graphDetails.push(detail);

                  if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                    detail.measurements.forEach((measurement) => {
                      measurement.registrationPointColor = registrationPointColors[nextIndex];
                      measurement.isProfit = detail.isProfit;
                      measurement.resourceName = project.resourceDescription;
                    });

                    projectMeasurements.push(detail.measurements);
                  }
                });

                // Add to graphdataResults.projects
                graphdataResults.projects.push(projectData);
              }

              return projectData;
            } catch (error) {
              console.error(`Error fetching project ${project.id} for date range:`, error);
              return null;
            }
          });

          // Wait for all unsaved projects to be fetched
          await Promise.all(unsavedProjectPromises);

          setGraphProjects(graphdataResults.projects);
          setGraphDetails(graphDetails);

          projectMeasurements = projectMeasurements.flat();
          let tempProjectMeasurementRows;

          tempProjectMeasurementRows = projectMeasurements.map((y) => ({
            accountId: y.accountId,
            accountName: y.accountName,
            accountCategoryId: y.accountCategoryId,
            accountCategoryName: y.accountCategoryName,
            checked: true,
            projectId: y.projectId,
            toDate: y.toDate,
            fromDate: y.fromDate,
            color: y.color,
            id: y.id,
            typeId: y.typeId,
            valueTypeId: y.valueTypeId,
            companyId: y.companyId,
            qrCode: y.qrCode,
            companyName: y.companyName,
            detailId: y.projectDetailId,
            projectName: y.projectName,
            resourceName: y.resourceName,
            registrationPointColor: y.registrationPointColor,
            description: y.description,
            durationInMinutes: getDurationInMinutes(y),
            startTime: y.startTime,
            startHour: getStartHour(y.startTime),
            startDayOfWeek: getStartDayOfWeek(y.startTime),
            stopTime: y.stopValue,
            insertedOn: new Date(y.insertedOn),
            updatedOn: y.stopTime,
            measurementSessionId: y.measurementSessionId,
            measurementValue: getMeasurementValue(
              y.typeId,
              y.externalApiTypeId,
              y.textValue,
              y.integerValue,
              y.dateValue,
              y.dataListValue,
              y.imageLink,
              y.externalApiValues,
              y.isNegative,
              y.numericValue
            ),
            integerValue: y.integerValue,
            running: y.running,
            thumbnail: y.thumbnail,
            imageId: y.imageId,
            commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
            commentImage: y.commentImage,
            commentId: y.commentId,
            valueType: getValueType(y.valueTypeId),
            keyValue: y.keyValue,
            eventPrice: y.eventPrice || 0,
            signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
            priceBundleId: y.priceBundleId,
            externalApiTypeId: y.externalApiTypeId,
            isProfit: y.isProfit,
            isNegative: y.isNegative,
            numericValue: y.numericValue,
            sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
            sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
          }));
          tempDataMeasurementRows.push(...tempProjectMeasurementRows.flat());

          tempFinancialMeasurementRows = tempDataMeasurementRows.filter((row) => row.eventPrice > 0);
          let filteredDataMeasurementRows = filterDataMeasurements(tempDataMeasurementRows);
          let filteredFinancialMeasurementRows = filterFinancialMeasurements(tempDataMeasurementRows.filter((row) => row.eventPrice > 0));

          graphdataResults.projects.forEach((project) => {
            tempGraphData.push(parseProjectData(project));
            project.details.forEach((detail) => {
              if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
                projectMeasurements.push(detail.measurements);
              }
            });
          });

          // Setting the states for measurements and graph data
          const selectionPromise = new Promise((resolve) => {
            const indexes = filteredDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            const profitLossIndexes = filteredFinancialMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setSelection(indexes);
            setProfitLossSelection(profitLossIndexes);
            resolve();
          });

          const measurementRowsPromise = new Promise((resolve) => {
            setDataMeasurementRows([...filteredDataMeasurementRows].flat());
            setFinancialMeasurementRows([...filteredFinancialMeasurementRows].flat());
            resolve();
          });

          const originalMeasurementRowsPromise = new Promise((resolve) => {
            const sortedRows = [...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn));
            setOriginalMeasurementRows(sortedRows);
            resetViewRangeToDefault(sortedRows);
            resolve();
          });

          // Data for yamazumi view
          const graphDataPromise = new Promise((resolve) => {
            setGraphData(parseMeasurementRows(filteredDataMeasurementRows).flat());
            resolve();
          });

          // Measurements for display
          const measurementsToDisplayPromise = new Promise((resolve) => {
            setMeasurementsToDisplay(filteredDataMeasurementRows.flat());
            resolve();
          });

          // Data for timeline view
          const chartRowsPromise = new Promise((resolve) => {
            let data = chartData(filteredDataMeasurementRows.flat());

            setChartRows(data);
            resolve();
          });

          // Data for financial view
          const financialDataPromise = new Promise((resolve) => {
            constructPriceData(graphdataResults.projects);

            let financialChartMeasurements = filteredDataMeasurementRows;

            let data = constructFinancialData(graphDetails, financialChartMeasurements);
            let detailNames = constructDetailCollection(graphDetails);
            setDetailsNames(detailNames);
            setFinancialChartData(data);

            // setting data for highlow table
            let tempGraphProjects = [...graphdataResults.projects]; // Clone the projects array

            tempGraphProjects = tempGraphProjects.map((project) => {
              return {
                ...project,
                details: project.details.map((detail) => {
                  return {
                    ...detail,
                    measurements: detail.measurements,
                  };
                }),
              };
            });
            setHighLowGraphProjects(tempGraphProjects);

            resolve();
          });

          var dateranges = { 0: dateRange };
          // Data for profit loss report
          const profitLossReportDataPromise = new Promise((resolve) => {
            let tempProfitLossReportData;
            formatProfitLossReportData(tempProjectMeasurementRows, dateranges).then((formattedData) => {
              tempProfitLossReportData = formattedData;
              setProfitLossReportData(tempProfitLossReportData);
            });

            resolve();
          });

          const selectedProjectsPromise = new Promise((resolve) => {
            setSelectedProjects(tempSelectedProjects);
            resolve();
          });

          // Wait for all state updates to complete before setting loading to false
          Promise.all([
            selectionPromise,
            measurementRowsPromise,
            originalMeasurementRowsPromise,
            graphDataPromise,
            measurementsToDisplayPromise,
            chartRowsPromise,
            financialDataPromise,
            profitLossReportDataPromise,
            selectedProjectsPromise,
          ]).then(() => {
            setFetchingData(false);
            
            // Fetch recurring financials after date range data is loaded
            fetchRecurringFinancials(tempDataMeasurementRows, dateRange);
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching graph configuration by date range:", error);
      })
      .finally(() => {
        setFetchingData(false);
      });
  };

  const setName = (event) => {
    setGraphConfigurationName(event.target.value);
  };

  const setDescription = (event) => {
    setGraphConfigurationDescription(event.target.value);
  };

  const getValueType = (id) => {
    switch (id) {
      case valueTypeCodes.VA:
        return "Value added";
      case valueTypeCodes.NVA:
        return "No value added";
      case valueTypeCodes.W:
        return "Waste";
      default:
        return "Unknown";
    }
  };

  const getMeasurementValue = (typeId, externalApiTypeId, text, int, time, dataListValue, imageLink, externalApiValue, isNegative, numericValue) => {
    switch (typeId) {
      case 1:
        return int;
      case 2:
        if (int > 0) {
          return time;
        } else {
          return "";
        }
      case 3:
        return text;
      case 4:
        return dataListValue;
      case 5:
        return text;
      case 6:
        return imageLink;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          if (int > 0) {
            return time;
          } else {
            return "";
          }
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          if (isNegative) {
            return `-${numericValue}`;
          } else {
            return numericValue;
          }
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return text;
        }
        return text;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getCommentText = (typeId, externalApiTypeId, text, commentText) => {
    switch (typeId) {
      case 1:
        return commentText;
      case 2:
        return commentText;
      case 3:
        return commentText;
      case 4:
        return commentText;
      case 5:
        return commentText;
      case 6:
        return commentText;
      case 7:
        if (externalApiTypeId === externalApiTypes.STOPWATCH) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.NUMERIC) {
          return text;
        } else if (externalApiTypeId === externalApiTypes.TEXT) {
          return commentText;
        }
        return commentText;
      default:
        return "";
      // eslint-disable-next-line
    }
  };

  const getLatestStartTimeElement = (data) => {
    //We check the latest measurement -> this is how we will decide from where to start the 'last 24h'
    if (data !== null && data.length > 0) {
      return data.reduce((latest, current) => {
        const latestTime = new Date(latest.startTime);
        const currentTime = new Date(current.startTime);

        // Check if current startTime is later than the latest
        return currentTime > latestTime ? current : latest;
      });
    } else {
      return null;
    }
  };

  const zoomMsMap = { "1 Day": 86400000, "1 Week": 7 * 86400000, "1 Month": 30 * 86400000, "3 Months": 90 * 86400000 };

  const resetViewRangeToDefault = (rows) => {
    if (extraParams.viewFrom && extraParams.viewTo) {
      setViewRangeDates({ fromDate: new Date(extraParams.viewFrom), endDate: new Date(extraParams.viewTo) });
      setTimeRangeZoom(extraParams.zoom || '1 Week');
      return;
    }
    try {
      const s = JSON.parse(localStorage.getItem(viewRangeKey));
      if (s?.viewFrom && s?.viewTo) {
        setViewRangeDates({ fromDate: new Date(s.viewFrom), endDate: new Date(s.viewTo) });
        setTimeRangeZoom(s.zoom || '1 Week');
        return;
      }
    } catch {}
    const latest = getLatestStartTimeElement(rows ?? originalMeasurementRows);
    const end = latest ? new Date(latest.startTime) : new Date();
    const from = new Date(end.getTime() - 7 * 86400000);
    setViewRangeDates({ fromDate: from, endDate: end });
    setTimeRangeZoom("1 Week");
  };

  const handleTimeRangeZoomChange = (zoom) => {
    setTimeRangeZoom(zoom);
    const duration = zoomMsMap[zoom] ?? 7 * 86400000;
    const latest = getLatestStartTimeElement(originalMeasurementRows);
    const end = latest ? new Date(latest.startTime) : (viewRangeDates.endDate ?? new Date());
    const from = new Date(end.getTime() - duration);
    setViewRangeDates({ fromDate: from, endDate: end });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom, viewFrom: from.toISOString(), viewTo: end.toISOString() })); } catch {}
    setExtraParam('zoom', zoom);
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', end.toISOString());
  };

  const handleTimeRangeScrollPrev = () => {
    const duration = zoomMsMap[timeRangeZoom] ?? 7 * 86400000;
    let from = new Date(viewRangeDates.fromDate.getTime() - duration);
    let to = new Date(viewRangeDates.endDate.getTime() - duration);
    if (timeRangeDates.fromDate && from < timeRangeDates.fromDate) {
      const overflow = timeRangeDates.fromDate.getTime() - from.getTime();
      from = new Date(timeRangeDates.fromDate);
      to = new Date(to.getTime() + overflow);
    }
    setViewRangeDates({ fromDate: from, endDate: to });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom: timeRangeZoom, viewFrom: from.toISOString(), viewTo: to.toISOString() })); } catch {}
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', to.toISOString());
  };

  const handleTimeRangeScrollNext = () => {
    const duration = zoomMsMap[timeRangeZoom] ?? 7 * 86400000;
    let from = new Date(viewRangeDates.fromDate.getTime() + duration);
    let to = new Date(viewRangeDates.endDate.getTime() + duration);
    if (timeRangeDates.toDate && to > timeRangeDates.toDate) {
      const overflow = to.getTime() - timeRangeDates.toDate.getTime();
      to = new Date(timeRangeDates.toDate);
      from = new Date(from.getTime() - overflow);
    }
    setViewRangeDates({ fromDate: from, endDate: to });
    try { localStorage.setItem(viewRangeKey, JSON.stringify({ zoom: timeRangeZoom, viewFrom: from.toISOString(), viewTo: to.toISOString() })); } catch {}
    setExtraParam('viewFrom', from.toISOString());
    setExtraParam('viewTo', to.toISOString());
  };

  const getMeasurementTime = (m) => {
    if (m.startTime) { const t = new Date(m.startTime).getTime(); if (!isNaN(t)) return t; }
    if (m.insertedOn) { const t = new Date(m.insertedOn).getTime(); if (!isNaN(t)) return t; }
    return null;
  };

  const filterByViewRange = (rows) => {
    if (!viewRangeDates?.fromDate || !viewRangeDates?.endDate) return rows;
    const from = viewRangeDates.fromDate.getTime();
    const to = viewRangeDates.endDate.getTime();
    return rows.filter((m) => {
      const t = getMeasurementTime(m);
      return t === null || (t >= from && t <= to);
    });
  };

  const setDateRange = (startDate, endDate) => {
    // check if both dates are valid
    if (startDate === null || startDate === undefined || endDate === null || endDate === undefined) {
      console.warn("Invalid date range provided. Both startDate and endDate must be valid dates.");
      return;
    } else if (!(startDate instanceof Date) || !(endDate instanceof Date) || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn("Invalid date range provided. Both startDate and endDate must be valid Date objects.");
      return;
    }

    setStartDate(startDate);
    setEndDate(endDate);

    if (startDate && endDate) {
      setTimeRangeDates({ fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) });
      setExtraParam('startDate', new Date(startDate).toISOString());
      setExtraParam('endDate', new Date(endDate).toISOString());
      let tempProfitLossDates = { 0: { sequence: 1, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) } };
      setProfitLossDates(tempProfitLossDates);

      // new daterange
      let newDateRange = { sequence: 1, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) };
      fetchConfigurationByDateRange(newDateRange);
      // onFiltersChange(null, newDateRange, tempProfitLossDates, null);
    } else {
      resetDateRangeFilter();
    }
  };

  const handleSetStartDate = (event) => {
    if (event !== startDate) {
      setStartDate(event);
      setDateRange(event, endDate);
    }
  };

  const handleSetEndDate = (event) => {
    if (event !== endDate) {
      setEndDate(event);
      setDateRange(startDate, event);
    }
  };

  const resetDateRangeFilter = () => {
    // First check if there's a saved date range in the graph configuration
    if (graphConfigurationObject && graphConfigurationObject.metaData) {
      const graphConfigurationMetaData = JSON.parse(graphConfigurationObject.metaData);
      if (graphConfigurationMetaData.dateRange !== null && graphConfigurationMetaData.dateRange !== undefined) {
        const savedFrom = new Date(graphConfigurationMetaData.dateRange.fromDate);
        const savedTo = new Date(graphConfigurationMetaData.dateRange.toDate);
        const tempTimeRangeDates = { fromDate: savedFrom, startDate: savedFrom, toDate: savedTo, endDate: savedTo, sequence: 1 };
        let tempProfitLossDates = { 0: tempTimeRangeDates };
        setStartDate(savedFrom);
        setEndDate(savedTo);
        setTimeRangeDates(tempTimeRangeDates);
        setProfitLossDates(tempProfitLossDates);
        setExtraParam('startDate', '');
        setExtraParam('endDate', '');

        // new date
        let newDateRange = { fromDate: savedFrom, startDate: savedFrom, toDate: savedTo, endDate: savedTo, sequence: 1 };
        fetchConfigurationByDateRange(newDateRange);
        return;
      }
    }

    // If no saved date range, fall back to 24 hours before last measurement
    let element = getLatestStartTimeElement(originalMeasurementRows);
    let from = timeRangeDates.fromDate;
    let to = timeRangeDates.toDate;
    let tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };

    if (element !== null && element !== undefined) {
      to = new Date(element.startTime);
      from = new Date(element.startTime);
      from.setHours(from.getHours() - 24);
      const formattedDate = from.toString();
      from = formattedDate;
      tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };
      let tempProfitLossDates = { 0: tempTimeRangeDates };
      setStartDate(new Date(from));
      setEndDate(new Date(to));
      setTimeRangeDates(tempTimeRangeDates);
      setProfitLossDates(tempProfitLossDates);
      setExtraParam('startDate', '');
      setExtraParam('endDate', '');

      // new date
      let newDateRange = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to, sequence: 1 };
      fetchConfigurationByDateRange(newDateRange);
      // onFiltersChange(null, newDateRange, tempProfitLossDates, null);
    }

    setPercentageVisible(false);
  };

  // Update Timerange for data
  const updateDataRange = (data, changeDay) => {
    let tempStartDate = new Date(data[0]);
    let tempEndDate = new Date(data[1]);
    let duplicateDateRange = JSON.parse(JSON.stringify(timeRangeDates));

    var objNewTimeRangeDate;

    if (changeDay) {
      // if it was a 'changeDay' action we start again with a 24h range
      objNewTimeRangeDate = { startDate: new Date(tempStartDate), fromDate: new Date(tempStartDate), toDate: new Date(tempEndDate), endDate: new Date(tempEndDate) };
    } else {
      // if slider was changed
      objNewTimeRangeDate = { startDate: new Date(tempStartDate), fromDate: new Date(duplicateDateRange.fromDate), toDate: new Date(duplicateDateRange.toDate), endDate: new Date(tempEndDate) };
    }

    if (JSON.stringify(objNewTimeRangeDate) !== JSON.stringify(timeRangeDates)) {
      setTimeRangeDates(objNewTimeRangeDate);
      setExtraParam('startDate', objNewTimeRangeDate.fromDate.toISOString());
      setExtraParam('endDate', objNewTimeRangeDate.toDate.toISOString());
      let tempProfitLossDates = objNewTimeRangeDate;
      tempProfitLossDates.sequence = 1;
      setProfitLossDates({ 0: tempProfitLossDates });

      onFiltersChange(null, objNewTimeRangeDate, { 0: tempProfitLossDates }, null);
    }
  };

  const getColorForType = (id) => {
    switch (id) {
      case valueTypeCodes.VA:
        return "#4caf50"; // Green for Value Added
      case valueTypeCodes.NVA:
        return "#ff9800"; // Orange for Non Value Added
      case valueTypeCodes.W:
        return "#f44336"; // Red for Waste
      default:
        return "#9e9e9e"; // Gray for Unknown
    }
  };

  const handleProjectSelection = (project) => {
    var graphdataResults = [];
    let tempGraphData = graphData;
    let tempMeasurementRows = JSON.parse(JSON.stringify(originalMeasurementRows));
    var projectMeasurements = [];
    let tempTimeRangeDates = timeRangeDates;
    let tempProfitLossDates = profitLossDates;
    let tempGraphDetails = graphDetails;
    let tempGraphProjects = graphProjects;

    let tempDataMeasurementRows;
    let tempFinancialMeasurementRows;

    if (!selectedProjects.some((x) => x.id === project.id)) {
      let tempGraphConfigurationData = JSON.parse(JSON.stringify(graphConfigurationData));
      let currentProjectData = {
        projectId: project.id,
        sequence: tempGraphConfigurationData.projects.length + 1,
      };
      tempGraphConfigurationData.projects.unshift(currentProjectData);
      let tempSelectedProjects = [];
      if (selectedProjects !== null && selectedProjects !== undefined) {
        tempSelectedProjects = JSON.parse(JSON.stringify(selectedProjects));
      }
      tempSelectedProjects.unshift(project);
      setSelectedProjects(tempSelectedProjects);
      setSelectedProject(project);

      if (tempSelectedProjects.length === 1) {
        // find currency in allCurrencies and setActiveCurrency to that currency
        let currency = allCurrencies.find((x) => x.id === tempSelectedProjects[0].currencyId);
        setActiveCurrency(currency);
      }

      //Fetch the project in the same way that is fetched on for the graphconfiguration.
      dataService.getAllProjectGraphMeasurementsByIdAndDateRange(project.id, timeRangeDates.fromDate.toISOString(), timeRangeDates.toDate.toISOString()).then((results) => {
        if (results) {
          //First we set the graph details
          tempGraphProjects.unshift(results);
          setGraphProjects(tempGraphProjects);
          project.details.forEach((detail) => {
            detail.resourceName = project.resourceDescription;
            detail.externalApiTypeId = results.details.find((x) => x.id === detail.id)?.externalApiTypeId || null; // Ensure externalApiTypeId is set
            tempGraphDetails.push(detail);
            if (detail.measurements !== null && detail.measurements !== undefined && detail.measurements.length > 0) {
              // get index of the new project in the array
              let index = tempGraphProjects.findIndex((x) => x.id === project.id);
              detail.measurements.forEach((measurement) => {
                measurement.registrationPointColor = registrationPointColors[index];
                measurement.isProfit = detail.isProfit;
                measurement.resourceName = project.resourceDescription;
              });

              projectMeasurements.push(detail.measurements);
            }
          });
          setGraphDetails(tempGraphDetails);

          projectMeasurements = projectMeasurements.flat();

          let tempProjectMeasurementRows = [];
          results.details.map((detail) => {
            detail.measurements.map((y) =>
              tempProjectMeasurementRows.push({
                accountId: y.accountId,
                accountName: y.accountName,
                accountCategoryId: y.accountCategoryId,
                accountCategoryName: y.accountCategoryName,
                checked: true,
                projectId: y.projectId,
                toDate: y.toDate,
                fromDate: y.fromDate,
                color: y.color,
                id: y.id,
                typeId: y.typeId,
                companyId: y.companyId,
                qrCode: y.qrCode,
                companyName: y.companyName,
                detailId: y.projectDetailId,
                projectName: y.projectName,
                resourceName: y.resourceName,
                registrationPointColor: y.registrationPointColor,
                description: y.description,
                durationInMinutes: getDurationInMinutes(y),
                startTime: y.startTime,
                startHour: getStartHour(y.startTime),
                startDayOfWeek: getStartDayOfWeek(y.startTime),
                stopTime: y.stopValue,
                insertedOn: new Date(y.startTime),
                updatedOn: y.stopTime,
                measurementSessionId: y.measurementSessionId,
                measurementValue: getMeasurementValue(
                  y.typeId,
                  y.externalApiTypeId,
                  y.textValue,
                  y.integerValue,
                  y.dateValue,
                  y.dataListValue,
                  y.imageLink,
                  y.externalApiValues,
                  y.isNegative,
                  y.numericValue
                ),
                integerValue: y.integerValue,
                running: y.running,
                thumbnail: y.thumbnail,
                imageId: y.imageId,
                commentText: getCommentText(y.typeId, y.externalApiTypeId, y.textValue, y.commentText),
                commentImage: y.commentImage,
                commentId: y.commentId,
                valueTypeId: y.valueTypeId,
                valueType: getValueType(y.valueTypeId),
                keyValue: y.keyValue,
                eventPrice: y.eventPrice || 0,
                signedEventPrice: y.isProfit ? (y.eventPrice || 0) : -(y.eventPrice || 0),
                priceBundleId: y.priceBundleId,
                externalApiTypeId: y.externalApiTypeId,
                isProfit: detail.isProfit,
                isNegative: y.isNegative,
                numericValue: y.numericValue,
                sessionValues: y.metadata ? JSON.parse(y.metadata).map(m => m.value).join(', ') : '',
                sessionMetadata: y.metadata ? JSON.parse(y.metadata) : [],
              })
            );
          });
          tempProjectMeasurementRows = tempMeasurementRows.unshift(...tempProjectMeasurementRows.flat());

          if (selectedProjects.length === 0) {
            // Setting timerangedates
            let element = getLatestStartTimeElement(tempMeasurementRows);
            let from = timeRangeDates.fromDate;
            let to = timeRangeDates.toDate;
            if (element !== null && element !== undefined) {
              to = new Date(element.startTime);
              from = new Date(element.startTime);
              from.setHours(from.getHours() - 24);
              const formattedDate = from.toString();
              from = formattedDate;
              tempTimeRangeDates = { fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to };
              tempProfitLossDates = { 0: { sequence: 1, fromDate: new Date(from), startDate: new Date(from), toDate: to, endDate: to } };
              setTimeRangeDates(tempTimeRangeDates);
              setProfitLossDates({ 0: tempTimeRangeDates });
            }
          }

          tempDataMeasurementRows = [...tempMeasurementRows];
          tempFinancialMeasurementRows = [...tempMeasurementRows].filter((row) => row.eventPrice > 0);

          // Setting the states for measurements and graph data
          const selectionPromise = new Promise((resolve) => {
            // const indexes = tempMeasurementRows.map((item, index) => item.checked ? index : -1).filter(index => index !== -1);

            let profitLossMeasurementsBetweenDate = tempDataMeasurementRows.filter((row) => row.eventPrice > 0);

            const indexes = tempDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setSelection(indexes);

            // ProfitLossSelection
            const profitLossIndexes = profitLossMeasurementsBetweenDate.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
            setProfitLossSelection(profitLossIndexes);
            resolve();
          });

          const measurementRowsPromise = new Promise((resolve) => {
            setDataMeasurementRows([...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            setFinancialMeasurementRows(
              [...tempDataMeasurementRows]
                .flat()
                .sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn))
                .filter((row) => row.eventPrice > 0)
            );
            resolve();
          });

          const originalMeasurementRowsPromise = new Promise((resolve) => {
            setOriginalMeasurementRows([...tempDataMeasurementRows].flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            resolve();
          });

          // Data for yamazumi
          const graphDataPromise = new Promise((resolve) => {
            setGraphData(parseMeasurementRows(tempDataMeasurementRows));
            resolve();
          });

          // Measurements for display
          const measurementsToDisplayPromise = new Promise((resolve) => {
            setMeasurementsToDisplay(tempDataMeasurementRows.flat().sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)));
            resolve();
          });

          // Data for timeline view
          const chartRowsPromise = new Promise((resolve) => {
            let data = chartData(tempDataMeasurementRows.flat());
            setChartRows(data);
            resolve();
          });

          // Data for financial view
          const financialDataPromise = new Promise((resolve) => {
            constructPriceData(tempGraphProjects, tempTimeRangeDates.fromDate, tempTimeRangeDates.toDate);

            let financialChartMeasurements = tempMeasurementRows
              .sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn))
              .filter((x) => roundToSecond(new Date(x.insertedOn)) > new Date(tempTimeRangeDates.fromDate) && roundToSecond(new Date(x.insertedOn)) <= new Date(tempTimeRangeDates.toDate));
            let data = constructFinancialData(tempGraphDetails, financialChartMeasurements);
            let detailNames = constructDetailCollection(tempGraphDetails);
            setDetailsNames(detailNames);
            setFinancialChartData(data);

            let fromDate = tempTimeRangeDates.fromDate;
            let toDate = tempTimeRangeDates.toDate;

            let temp2GraphProjects = tempGraphProjects.map((project) => {
              return {
                ...project,
                details: project.details.map((detail) => {
                  return {
                    ...detail,
                    measurements: detail.measurements
                      .filter((measurement) => roundToSecond(new Date(measurement.insertedOn)) > fromDate && roundToSecond(new Date(measurement.insertedOn)) < toDate)
                      .map((measurement) => ({
                        ...measurement,
                        typeId: detail.controlTypeId, // Set typeId to controlTypeId
                      })),
                  };
                }),
              };
            });
            setHighLowGraphProjects(temp2GraphProjects);
            resolve();
          });

          // Data for profit loss report
          const profitLossReportDataPromise = new Promise((resolve) => {
            let tempProfitLossReportData;
            formatProfitLossReportData(tempMeasurementRows, tempProfitLossDates).then((formattedData) => {
              tempProfitLossReportData = formattedData;
              setProfitLossReportData(tempProfitLossReportData);
            });

            resolve();
          });

          const timeRangeDatesPromise = new Promise((resolve) => {
            setTimeRangeDates(tempTimeRangeDates);
            setProfitLossDates(tempProfitLossDates);
            resolve();
          });

          const selectedProjectsPromise = new Promise((resolve) => {
            setSelectedProjects(tempSelectedProjects);
            resolve();
          });

          // Wait for all state updates to complete before setting loading to false
          Promise.all([
            selectionPromise,
            measurementRowsPromise,
            graphDataPromise,
            originalMeasurementRowsPromise,
            measurementsToDisplayPromise,
            chartRowsPromise,
            financialDataPromise,
            profitLossReportDataPromise,
            timeRangeDatesPromise,
            selectedProjectsPromise,
          ]).then(() => {
            setLoading(false); // Only set loading to false after all updates
            
            // Fetch recurring financials after project data is loaded
            fetchRecurringFinancials(tempMeasurementRows, tempTimeRangeDates);
          });
        }
      });

      setGraphConfigurationData(tempGraphConfigurationData);
      setOrderedProjectNames((prev) => [...prev, project.resourceDescription]);
    }
  };

  const removeSelectedProject = (projectId) => {
    const removedProject = selectedProjects.find((p) => p.id === projectId);
    let tempSelectedProjects = JSON.parse(JSON.stringify(selectedProjects));
    let tempGraphData = JSON.parse(JSON.stringify(graphData));
    let tempGraphConfigurationData = JSON.parse(JSON.stringify(graphConfigurationData));
    let tempOriginalMeasurementRows = JSON.parse(JSON.stringify(originalMeasurementRows));
    let tempFinancialChartData = JSON.parse(JSON.stringify(financialChartData));
    let tempPriceData = JSON.parse(JSON.stringify(priceData));
    let tempChartRows = JSON.parse(JSON.stringify(chartRows));
    let tempHighLowGraphProjects = JSON.parse(JSON.stringify(highLowGraphProjects));

    let tempDataMeasurementRows = JSON.parse(JSON.stringify(dataMeasurementRows));
    let tempFinancialMeasurementRows = JSON.parse(JSON.stringify(financialMeasurementRows));

    let tempGraphProjects = JSON.parse(JSON.stringify(graphProjects));

    tempGraphProjects = tempGraphProjects.filter((x) => x.id !== projectId);

    // Filter out the project with the matching projectId from selectedProjects
    tempSelectedProjects = tempSelectedProjects.filter((x) => x.id !== projectId);

    // Filter out the measurements from originalMeasurementRows
    tempOriginalMeasurementRows = tempOriginalMeasurementRows.filter((measurement) => measurement.projectId !== projectId);

    // Filter out Data Measurements
    tempDataMeasurementRows = tempDataMeasurementRows.filter((measurement) => measurement.projectId !== projectId);
    const indexes = tempDataMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setSelection(indexes);

    // Filter out Financial Measurements
    tempFinancialMeasurementRows = tempFinancialMeasurementRows.filter((measurement) => measurement.projectId !== projectId);
    const profitLossIndexes = tempFinancialMeasurementRows.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setProfitLossSelection(profitLossIndexes);

    // // Filter out the measurements
    // tempMeasurementRows = tempMeasurementRows.filter(measurement => measurement.projectId !== projectId);
    // const indexes = tempMeasurementRows.map((item, index) => item.checked ? index : -1).filter(index => index !== -1);
    // setSelection(indexes);
    // Filter out profit/loss measurements
    // tempProfitLossMeasurementRows = tempProfitLossMeasurementRows.filter(measurement => measurement.projectId !== projectId);
    // const profitLossIndexes = tempProfitLossMeasurementRows.map((item, index) => item.checked ? index : -1).filter(index => index !== -1);
    // setProfitLossSelection(profitLossIndexes);

    // Filter out the entries in graphConfigurationData
    tempGraphConfigurationData.projects = tempGraphConfigurationData.projects.filter((data) => data.projectId !== projectId);

    // Filter out priceData
    tempPriceData = tempPriceData.filter((data) => data.projectId !== projectId);

    // Filter out financialData
    tempFinancialChartData = tempFinancialChartData.filter((data) => data.projectId !== projectId);

    // Filter out chartRows
    tempChartRows = tempChartRows.filter((data) => data.projectId !== projectId);

    // Refetch recurring financials with the updated measurement rows
    fetchRecurringFinancials(tempDataMeasurementRows, timeRangeDates);

    // Set ProfitLossReportData
    let tempProfitLossReportData;

    formatProfitLossReportData(tempOriginalMeasurementRows, profitLossDates).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });

    // Filter out highLowProjects
    tempHighLowGraphProjects = tempHighLowGraphProjects.filter((project) => project.id !== projectId);

    var graphDetails = [];
    tempSelectedProjects.map((project) => {
      project.details.map((detail) => {
        graphDetails.push(detail);
      });
    });

    if (tempSelectedProjects.length === 0) {
      setActiveCurrency(null);
    }

    let detailNames = constructDetailCollection(graphDetails);
    setGraphDetails(graphDetails);
    setDetailsNames(detailNames);
    setGraphData(parseMeasurementRows(tempDataMeasurementRows).flat()); // Update graph data (Yamazumi)
    setMeasurementsToDisplay(tempDataMeasurementRows.flat());
    setSelectedProjects(tempSelectedProjects);
    setGraphConfigurationData(tempGraphConfigurationData);
    setPriceData(tempPriceData);
    setFinancialChartData(tempFinancialChartData);
    setChartRows(tempChartRows);
    setGraphProjects(tempGraphProjects);
    setHighLowGraphProjects(tempHighLowGraphProjects);
    setOriginalMeasurementRows(tempOriginalMeasurementRows);
    setDataMeasurementRows(tempDataMeasurementRows);
    setFinancialMeasurementRows(tempFinancialMeasurementRows);
    setOrderedProjectNames((prev) => prev.filter((name) => name !== removedProject?.resourceDescription));

    // onFiltersChange()
  };

  const formatProfitLossReportData = async (measurements, dateranges) => {
    // Merge recurring financials if toggle is on
    if (showRecurringPrices && recurringFinancials.length > 0) {
      measurements = [...measurements, ...recurringFinancials];
    }

    let accounts = [];
    let accountCategories = [];
    let tempProfitLossReportData = {
      companies: [],
      revenueAccountCategoryResults: [],
      costAccountCategoryResults: [],
      totalProfit: {},
      revenueTotal: {}, // Added for overall revenue totals
      costTotal: {}, // Added for overall cost totals
      percentageTotalProfit: {},
    };
    // Fetch accounts and categories if not already loaded
    if (!allAccounts.length && !allAccountCategories.length) {
      try {
        const [fetchedAccounts] = await Promise.all([dataService.getAllUserCompanyAccounts(props.Login.userid, 0, 0)]);

        let tempAccounts = [];
        let tempAccountCategories = [];

        fetchedAccounts.forEach((company) => {
          company.accountCategories.forEach((category) => {
            let tempCategory = {
              id: category.id,
              name: category.name,
              isRevenue: category.isRevenue,
              isActive: category.isActive,
              companyId: category.companyId,
              companyName: company.name,
              accounts: category.accounts,
            };
            tempAccountCategories.push(tempCategory);

            category.accounts.forEach((account) => {
              // Check if account already exists in tempAccounts
              let existingAccount = tempAccounts.find((acc) => acc.id === account.id);
              if (!existingAccount) {
                tempAccounts.push(account);
              }
            });
          });
        });

        setAllAccounts(tempAccounts);
        setAllAccountCategories(tempAccountCategories);

        accounts = tempAccounts;
        accountCategories = tempAccountCategories;
      } catch (error) {
        console.error("Error fetching account data:", error);
        return tempProfitLossReportData;
      }
    } else {
      accounts = allAccounts;
      accountCategories = allAccountCategories;
    }

    // sort dateranges by sequence
    let orderedDateRanges = Object.keys(dateranges)
      .map((key) => dateranges[key])
      .sort((a, b) => a.sequence - b.sequence);

    // Initialize date range totals and process measurements
    Object.keys(orderedDateRanges).forEach((rangeIndex) => {
      const { fromDate, toDate } = orderedDateRanges[rangeIndex];
      const from = new Date(fromDate);
      const to = new Date(toDate);

      // Initialize date range totals if not already initialized
      if (!tempProfitLossReportData.totalProfit[rangeIndex]) {
        tempProfitLossReportData.totalProfit[rangeIndex] = 0;
      }
      if (!tempProfitLossReportData.revenueTotal[rangeIndex]) {
        tempProfitLossReportData.revenueTotal[rangeIndex] = 0;
      }
      if (!tempProfitLossReportData.costTotal[rangeIndex]) {
        tempProfitLossReportData.costTotal[rangeIndex] = 0;
      }

      // Filter measurements within the current date range
      const measurementsInDateRange = measurements.filter((measurement) => {
        const insertedDate = roundToSecond(new Date(measurement.insertedOn));
        return insertedDate > from && insertedDate <= to;
      });

      // Process each measurement within the current date range
      measurementsInDateRange
        .filter((x) => x.accountCategoryId !== null)
        .forEach((measurement) => {
          let measurementAccount = accounts.find((acc) => acc.id === measurement.accountId);
          if (!measurementAccount) return;
          let measurementAccountCategory = accountCategories.find((category) => category.id === measurementAccount.accountCategoryId);

          if (!measurementAccountCategory) return;

          let measurementCompanyName = measurement.companyName;

          // Add company if not already present
          if (!tempProfitLossReportData.companies.includes(measurementCompanyName)) {
            tempProfitLossReportData.companies.push(measurementCompanyName);
            tempProfitLossReportData.companies.sort((a, b) => a.localeCompare(b));
          }

          const isRevenue = measurementAccountCategory.isRevenue;
          const categoryList = isRevenue ? tempProfitLossReportData.revenueAccountCategoryResults : tempProfitLossReportData.costAccountCategoryResults;

          let category = categoryList.find((cat) => cat.id === measurementAccountCategory.id);

          // Add category if not already present
          if (!category) {
            category = {
              id: measurementAccountCategory.id,
              name: measurementAccountCategory.name,
              isRevenue: isRevenue,
              accountResults: [],
              total: {}, // Dynamically calculated totals for each date range
              percentage: {},
            };
            categoryList.push(category);
          }

          let account = category.accountResults.find((acc) => acc.id === measurementAccount.id);

          // Add account if not already present
          if (!account) {
            account = {
              id: measurementAccount.id,
              name: measurementAccount.name,
              isActive: measurementAccount.isActive,
              total: {},
              percentage: {},
            };
            category.accountResults.push(account);
          }

          // Update totals
          account.total[rangeIndex] = (account.total[rangeIndex] || 0) + (measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);
          category.total[rangeIndex] = (category.total[rangeIndex] || 0) + (measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);

          if (isRevenue) {
            tempProfitLossReportData.revenueTotal[rangeIndex] += measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice;
          } else {
            tempProfitLossReportData.costTotal[rangeIndex] += measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice;
          }

          tempProfitLossReportData.totalProfit[rangeIndex] =
            (tempProfitLossReportData.totalProfit[rangeIndex] || 0) + (isRevenue === measurement.isNegative ? -measurement.eventPrice : measurement.eventPrice);
        });

      // Ensure totals for each category and account have all date ranges
      tempProfitLossReportData.revenueAccountCategoryResults.forEach((category) => {
        Object.keys(orderedDateRanges).forEach((rangeIndex) => {
          if (!category.total[rangeIndex]) {
            category.total[rangeIndex] = 0; // Set missing total to 0
          }
          category.accountResults.forEach((account) => {
            if (!account.total[rangeIndex]) {
              account.total[rangeIndex] = 0; // Set missing account total to 0
            }
          });
        });
      });

      tempProfitLossReportData.costAccountCategoryResults.forEach((category) => {
        Object.keys(orderedDateRanges).forEach((rangeIndex) => {
          if (!category.total[rangeIndex]) {
            category.total[rangeIndex] = 0; // Set missing total to 0
          }
          category.accountResults.forEach((account) => {
            if (!account.total[rangeIndex]) {
              account.total[rangeIndex] = 0; // Set missing account total to 0
            }
          });
        });
      });

      // Ensure overall totals for revenue and cost have all date ranges
      Object.keys(orderedDateRanges).forEach((rangeIndex) => {
        if (!tempProfitLossReportData.revenueTotal[rangeIndex]) {
          tempProfitLossReportData.revenueTotal[rangeIndex] = 0;
        }
        if (!tempProfitLossReportData.costTotal[rangeIndex]) {
          tempProfitLossReportData.costTotal[rangeIndex] = 0;
        }
      });
    });

    // Add percentage calculations for each category and account
    const calculatePercentage = (prev, curr) => {
      if (prev === 0 && curr === 0) return 0; // No change
      if (prev === 0) return 100; // From 0 to any value
      return ((curr - prev) / Math.abs(prev)) * 100;
    };

    // Calculate percentages for each category
    [tempProfitLossReportData.revenueAccountCategoryResults, tempProfitLossReportData.costAccountCategoryResults].forEach((categoryList) => {
      categoryList.forEach((category) => {
        Object.keys(category.total).forEach((rangeIndex, idx, rangeKeys) => {
          if (idx > 0) {
            const prevIndex = rangeKeys[idx - 1];
            category.percentage[rangeIndex] = calculatePercentage(category.total[prevIndex] || 0, category.total[rangeIndex] || 0);
          }
        });

        category.accountResults.forEach((account) => {
          Object.keys(account.total).forEach((rangeIndex, idx, rangeKeys) => {
            if (idx > 0) {
              const prevIndex = rangeKeys[idx - 1];
              account.percentage[rangeIndex] = calculatePercentage(account.total[prevIndex] || 0, account.total[rangeIndex] || 0);
            }
          });
        });
      });
    });

    // Calculate percentages for totalProfit
    tempProfitLossReportData.percentageTotalProfit = {};
    Object.keys(tempProfitLossReportData.totalProfit).forEach((rangeIndex, idx, rangeKeys) => {
      if (idx > 0) {
        const prevIndex = rangeKeys[idx - 1];
        tempProfitLossReportData.percentageTotalProfit[rangeIndex] = calculatePercentage(tempProfitLossReportData.totalProfit[prevIndex] || 0, tempProfitLossReportData.totalProfit[rangeIndex] || 0);
      }
    });
    return tempProfitLossReportData;
  };

  const parseProjectData = (project) => {
    if (project) {
      const projectName = project.projectName;
      var projectValues = [];
      let dataSource = {};

      project.details.forEach((detail) => {
        if ((detail.controlTypeId === controlTypes.STOPWATCH || (detail.controlTypeId === controlTypes.EXTERNALAPI && detail.externalApiTypeId === externalApiTypes.STOPWATCH)) && detail.isActive) {
          let totalDuration = 0;
          var measurementCounter = 0;
          let totalPrice = 0;

          detail.measurements.forEach((measurement) => {
            if (measurement.stopValue !== null && measurement.stopValue !== undefined) {
              const start = roundToSecond(new Date(measurement.startTime));
              const stop = roundToSecond(new Date(measurement.stopValue));

              // Calculate duration in hours
              const duration = (stop - start) / 3600000; // 3600000 ms = 1 hour
              totalDuration += duration;
              totalPrice += measurement.price * duration; // Calculate price for the duration in hours
              measurementCounter += 1;
            }
          });

          dataSource[detail.description] = totalDuration;

          projectValues.push({
            projectId: project.id,
            state: projectName + " - " + project.resourceDescription,
            [detail.valueTypeId === valueTypeCodes.VA ? "VA" : detail.valueTypeId === valueTypeCodes.NVA ? "NVA" : detail.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN"]: totalDuration, // Dynamic key
            type: detail.valueTypeId,
            typeName: detail.valueTypeId === valueTypeCodes.VA ? "VA" : detail.valueTypeId === valueTypeCodes.NVA ? "NVA" : detail.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN",
            title: detail.description,
            color: getColorForType(detail.valueTypeId),
            measurements: measurementCounter,
            totalPrice: totalPrice,
          });
        }
      });

      return projectValues;
    }
  };

  const parseMeasurementRows = (measurementRows) => {
    // Initialize an object to hold the parsed data for each project
    let projectData = {};
    // Loop through each measurement row in the array
    measurementRows.forEach((row) => {
      // Only process rows where "checked" is true
      if (!row.checked || row.stopTime === null) return;

      if (row.typeId === controlTypes.STOPWATCH || (row.typeId === controlTypes.EXTERNALAPI && row.externalApiTypeId === externalApiTypes.STOPWATCH)) {
        const projectId = row.projectId;
        const detailId = row.detailId;
        const startTime = new Date(row.startTime);
        const stopTime = new Date(row.stopTime);
        const duration = (stopTime - startTime) / 3600000; // Calculate duration in hours
        const eventPrice = row.eventPrice;

        // Initialize data for this project if not already done
        if (!projectData[projectId]) {
          projectData[projectId] = {
            state: row.projectName + " - " + row.resourceName,
            values: {},
          };
        }

        // Check if this detailId has already been processed for this project
        if (!projectData[projectId].values[detailId]) {
          projectData[projectId].values[detailId] = {
            typeId: row.valueTypeId,
            title: row.description,
            color: row.color,
            typeName: row.valueTypeId === valueTypeCodes.VA ? "VA" : row.valueTypeId === valueTypeCodes.NVA ? "NVA" : row.valueTypeId === valueTypeCodes.W ? "W" : "UNKNOWN",
            totalDuration: 0,
            measurementCounter: 0,
            totalPrice: 0,
          };
        }

        if (duration > 0) {
          // Update the total duration and measurement counter for this detail
          projectData[projectId].values[detailId].totalPrice += eventPrice;
          projectData[projectId].values[detailId].totalDuration += duration;
          projectData[projectId].values[detailId].measurementCounter += 1;
        }
      }
    });

    // Transform the data into the required output format
    const parsedOutput = [];
    Object.values(projectData).forEach((project) => {
      Object.values(project.values).forEach((detail) => {
        parsedOutput.push({
          state: project.state,
          [detail.typeName]: detail.totalDuration, // Dynamic key based on typeName
          type: detail.typeId,
          typeName: detail.typeName,
          title: detail.title,
          color: detail.color,
          measurements: detail.measurementCounter,
          totalPrice: detail.totalPrice,
        });
      });
    });

    return parsedOutput;
  };

  const getEmptyStateTitle = () => {
    if (selectedProjects.length === 0) {
      return t('modifyGraph.noProjectsSelected');
    } else {
      return t('modifyGraph.noMeasurements');
    }
  };

  const getEmptyStateSubtitle = () => {
    if (selectedProjects.length === 0) {
      return t('modifyGraph.selectProjectHint');
    } else {
      return t('modifyGraph.noMeasurementsSubtitle');
    }
  };

  const resetErrors = () => {
    setGraphConfigurationNameError("");
    setSelectedProjectsError("");
  };

  const validateForm = () => {
    resetErrors();
    let errors = 0;

    // Name
    if (graphConfigurationName.trim() === "") {
      errors++;
      setGraphConfigurationNameError(t('modifyGraph.nameRequired'));
    } else if (graphConfigurationName.trim().length < 3) {
      errors++;
      setGraphConfigurationNameError(t('modifyGraph.nameMinLength'));
    }

    // Selected company
    if (!graphConfigurationCompanyId) {
      errors++;
      setGraphConfigurationCompanyIdError(t('modifyGraph.selectCompanyError'));
    }

    // Selected projects
    if (selectedProjects.length === 0) {
      errors++;
      setSelectedProjectsError(t('modifyGraph.selectProjectError'));
    }

    if (errors > 0) {
      return false;
    } else {
      return true;
    }
  };

  const saveGraphConfiguration = () => {
    if (validateForm()) {
      const configToSave = { ...graphConfigurationData };
      if (create) {
        dataService
          .createNewGraphConfiguration(graphConfigurationCompanyId, graphConfigurationName, graphConfigurationDescription, JSON.stringify(configToSave), false, props.Login.userid)
          .then((result) => {
            navigate("/graph/" + result.createmodel.uniqueId);
          })
          .catch((error) => {
            if (error.status == 403) {
              toast.error(t('errors.unauthorized'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            } else {
              toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            }
          });
      } else {
        dataService
          .updateGraphConfiguration(
            graphConfigurationId,
            graphConfigurationCompanyId,
            graphConfigurationName,
            graphConfigurationDescription,
            JSON.stringify(configToSave),
            graphIsPublished,
            props.Login.userid
          )
          .then((result) => {
            if (result && result.success) {
              toast.success(t('modifyGraph.saveSuccess'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
              navigate("/graph/" + graphConfigurationUniqueId);
            } else {
              toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            }
          })
          .catch((error) => {
            if (error.status == 403) {
              toast.error(t('errors.unauthorized'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            } else {
              toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
            }
          });
      }
    }
  };

  const getShareUrl = () => window.location.href.replace("/modifygraph/", "/viewgraph/");

  const handleEnableSharing = () => {
    setSharingLoading(true);
    dataService
      .publishGraphConfiguration(graphConfigurationId, true)
      .then((result) => {
        if (result.success) {
          setGraphIsPublished(true);
          toast.success(t('modifyGraph.sharingEnabled'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        } else {
          toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        }
      })
      .catch(() => {
        toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      })
      .finally(() => {
        setSharingLoading(false);
      });
  };

  const handleDisableSharing = () => {
    setSharingLoading(true);
    dataService
      .publishGraphConfiguration(graphConfigurationId, false)
      .then((result) => {
        if (result.success) {
          setGraphIsPublished(false);
          toast.success(t('modifyGraph.sharingDisabled'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        } else {
          toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        }
      })
      .catch(() => {
        toast.error(t('errors.generic'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
      })
      .finally(() => {
        setSharingLoading(false);
      });
  };

  //DevEx functions
  const onSelectionChange = (e) => {
    var tempRows = JSON.parse(JSON.stringify(dataMeasurementRows));

    const selectedIds = e.map((index) => (tempRows[index] !== undefined ? tempRows[index].id : null));
    setSelection(e);
    const updatedArray = tempRows.map((item) => (selectedIds.includes(item.id) ? { ...item, checked: true } : { ...item, checked: false }));

    var data = chartData(updatedArray.filter((x) => x.checked));

    if (JSON.parse(JSON.stringify(data)) !== JSON.parse(JSON.stringify(chartRows))) {
      setChartRows(data);
    }

    let tempProfitLossReportData;

    formatProfitLossReportData(
      updatedArray.filter((x) => x.checked),
      profitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });
    setGraphData(parseMeasurementRows(updatedArray.filter((x) => x.checked)));

    setMeasurementsToDisplay(updatedArray.filter((x) => x.checked));

    // Financial
    let financialChartMeasurements = updatedArray.sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn)).filter((x) => x.checked);
    let financialData = constructFinancialData(graphDetails, financialChartMeasurements);
    setFinancialChartData(financialData);
  };

  const onProfitLossSelectionChange = (e) => {
    var tempRows = JSON.parse(JSON.stringify(financialMeasurementRows));

    const selectedIds = e.map((index) => (tempRows[index] !== undefined ? tempRows[index].id : null));
    setProfitLossSelection(e);
    const updatedArray = tempRows.map((item) => (selectedIds.includes(item.id) ? { ...item, checked: true } : { ...item, checked: false }));

    let tempProfitLossReportData;

    formatProfitLossReportData(
      updatedArray.filter((x) => x.checked),
      profitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });
  };

  const filterDataMeasurements = (measurements) => {
    let filteredMeasurements = JSON.parse(JSON.stringify(measurements)); // Clone original rows

    filteredMeasurements.forEach((x) => (x.checked = true)); // Reset checked state

    // FILTERS
    dataFilters &&
      dataFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // Remove unchecked measurements
    filteredMeasurements = filteredMeasurements.filter((x) => x.checked);

    let dateSortedDataMeasurements = [...filteredMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    return dateSortedDataMeasurements;
  };

  const filterFinancialMeasurements = (measurements) => {
    let filteredMeasurements = measurements; // Clone original rows

    filteredMeasurements.forEach((x) => (x.checked = true)); // Reset checked state

    // FILTERS
    financialFilters &&
      financialFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // Remove unchecked measurements
    filteredMeasurements = filteredMeasurements.filter((x) => x.checked);

    let dateSortedFinancialMeasurements = [...filteredMeasurements].sort((a, b) => new Date(a.insertedOn).getTime() - new Date(b.insertedOn).getTime());
    return dateSortedFinancialMeasurements;
  };

  const onFiltersChange = (filters, dateRange, profitLossDateRanges, projection) => {
    let currentFilters;
    let currentDateRange;
    let currentProfitLossDates;

    let currentDataFilters;
    let currentFinancialFilters;

    let filteredDataMeasurements;
    let filteredFinancialMeasurements;

    let currentProjection;

    if (projection) {
      currentProjection = projection;
      setActiveProjection(JSON.parse(JSON.stringify(projection)));
    } else if (projection === "reset") {
      currentProjection = null;
      setActiveProjection(null);
    } else {
      currentProjection = activeProjection;
    }

    if (filters) {
      if (selectedGraphType === graphTypes.OUTPUT || selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.TIMELINE) {
        currentDataFilters = filters;
        setDataFilters(filters);
        currentFinancialFilters = financialFilters;
      } else if (selectedGraphType === graphTypes.FINANCIAL || selectedGraphType === graphTypes.PROFITLOSS) {
        currentFinancialFilters = filters;
        setFinancialFilters(filters);
        currentDataFilters = dataFilters;
      }
      currentFilters = filters;
      setAllFilters(filters);
    } else {
      currentDataFilters = dataFilters;
      currentFinancialFilters = financialFilters;
      currentFilters = allFilters;
    }

    if (dateRange) {
      currentDateRange = dateRange;
    } else {
      currentDateRange = timeRangeDates;
    }

    if (profitLossDateRanges) {
      currentProfitLossDates = profitLossDateRanges;
      setProfitLossDates(profitLossDateRanges);
    } else {
      currentProfitLossDates = profitLossDates;
    }

    let varFilteredResults = JSON.parse(JSON.stringify(originalMeasurementRows)).sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)); // Clone original rows

    varFilteredResults = varFilteredResults.filter((row) => {
      const startTime = roundToSecond(new Date(row.insertedOn)); // Convert to Date
      return startTime > currentDateRange.fromDate && startTime <= currentDateRange.toDate; // Check range
    });

    varFilteredResults.forEach((x) => (x.checked = true)); // Reset checked state

    // Uncheck all rows that have a date outside of date indicated in dateRangeSlider
    varFilteredResults
      .filter((row) => {
        const startTime = roundToSecond(new Date(row.insertedOn)); // Convert to Date
        return startTime > currentDateRange.endDate || startTime <= currentDateRange.startDate; // Check range
      })
      .forEach((x) => (x.checked = false));

    filteredDataMeasurements = varFilteredResults.filter((x) => x.checked).map((x) => ({ ...x })); // Creates new object copies

    filteredFinancialMeasurements = varFilteredResults.filter((x) => x.checked && x.eventPrice > 0).map((x) => ({ ...x })); // Creates new object copies

    // Add projectedEventValue calculation
    if (currentProjection) {
      filteredDataMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = calculateProjection(measurement, currentProjection); // Add your projection logic here
      });

      filteredFinancialMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = calculateProjection(measurement, currentProjection); // Add your projection logic here
      });
    } else {
      filteredDataMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = null; // Reset projectedEventPrice if no projection is set
      });

      filteredFinancialMeasurements.forEach((measurement) => {
        measurement.projectedEventPrice = null; // Reset projectedEventPrice if no projection is set
      });
    }

    currentDataFilters &&
      currentDataFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredDataMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredDataMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredDataMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredDataMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredDataMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredDataMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredDataMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredDataMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredDataMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredDataMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredDataMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredDataMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }

            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredDataMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredDataMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredDataMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredDataMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    currentFinancialFilters &&
      currentFinancialFilters.forEach((filter) => {
        switch (filter.columnName) {
          case "accountName":
            filteredFinancialMeasurements.filter((x) => x.accountName && !x.accountName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "accountCategoryName":
            filteredFinancialMeasurements.filter((x) => x.accountCategoryName && !x.accountCategoryName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "companyName":
            filteredFinancialMeasurements.filter((x) => !x.companyName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "projectName":
            filteredFinancialMeasurements.filter((x) => !x.projectName.toLowerCase().includes(filter.value.toLowerCase())).forEach((x) => (x.checked = false));
            break;
          case "commentText":
            filteredFinancialMeasurements
              .filter((x) => !(x.commentText && x.commentText.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          case "resourceName":
            if (filteredFinancialMeasurements.some((x) => !x.resourceName.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredFinancialMeasurements
                .filter((vfr) => !vfr.resourceName.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "description":
            if (filteredFinancialMeasurements.some((x) => !x.description.toLowerCase().includes(filter.value.toLowerCase()))) {
              filteredFinancialMeasurements
                .filter((vfr) => !vfr.description.toLowerCase().includes(filter.value.toLowerCase()))
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "valueType":
            var valueType = null;
            var waste = "waste";
            var valueAdded = "value added";
            var noValueAdded = "no value added";

            if (waste.includes(filter.value.toLowerCase())) {
              valueType = valueTypeCodes.W;
            } else if (valueAdded.includes(filter.value.toLowerCase()) && !filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.VA;
            } else if (noValueAdded.includes(filter.value.toLowerCase()) && filter.value.toLowerCase().includes("n")) {
              valueType = valueTypeCodes.NVA;
            }
            if (valueType !== null) {
              filteredFinancialMeasurements
                .filter((x) => x.valueTypeId !== valueType)
                .map((vfr) => {
                  vfr.checked = false;
                });
            }
            break;
          case "keyValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startDayOfWeek":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.startDayOfWeek && x.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.startDayOfWeek && vfr.startDayOfWeek.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "startTime":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (filteredFinancialMeasurements.some((x) => !(x.keyValue && x.keyValue.toLowerCase().includes(filter.value.toLowerCase())))) {
                filteredFinancialMeasurements
                  .filter((vfr) => !(vfr.keyValue && vfr.keyValue.toLowerCase().includes(filter.value.toLowerCase())))
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }
            break;
          case "measurementValue":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              if (
                filteredFinancialMeasurements.some(
                  (x) => !(x.measurementValue && x.measurementValue !== undefined && x.measurementValue !== "" && x.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                )
              ) {
                filteredFinancialMeasurements
                  .filter(
                    (vfr) =>
                      !(vfr.measurementValue && vfr.measurementValue !== undefined && vfr.measurementValue !== "" && vfr.measurementValue.toString().toLowerCase().includes(filter.value.toLowerCase()))
                  )
                  .map((vfr) => {
                    vfr.checked = false;
                  });
              }
            }

            break;
          case "signedEventPrice":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.signedEventPrice > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "startHour":
            // Filter for every operation in numericFilterOperations
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.startHour > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "durationInMinutes":
            if (filter.value !== "" && filter.value !== undefined && filter.value !== null) {
              const filterValue = parseFloat(filter.value);
              if (!isNaN(filterValue)) {
                let filteredItems;
                switch (filter.operation) {
                  case numericFilterOperations.equal:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes !== filterValue);
                    break;
                  case numericFilterOperations.notEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes === filterValue);
                    break;
                  case numericFilterOperations.greaterThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes <= filterValue);
                    break;
                  case numericFilterOperations.greaterThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes < filterValue);
                    break;
                  case numericFilterOperations.lessThan:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes >= filterValue);
                    break;
                  case numericFilterOperations.lessThanOrEqual:
                    filteredItems = filteredFinancialMeasurements.filter((x) => x.durationInMinutes > filterValue);
                    break;
                  default:
                    filteredItems = [];
                }
                filteredItems.forEach((x) => (x.checked = false));
              }
            }
            break;
          case "sessionValues":
            filteredFinancialMeasurements
              .filter((x) => !(x.sessionValues && x.sessionValues.toLowerCase().includes(filter.value.toLowerCase())))
              .forEach((x) => {
                x.checked = false;
              });
            break;
          default:
        }
      });

    // GENERAL

    let dateSortedDataMeasurements = [...filteredDataMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    let dateSortedFinancialMeasurements = [...filteredFinancialMeasurements].sort((a, b) => new Date(b.insertedOn).getTime() - new Date(a.insertedOn).getTime());

    setDataMeasurementRows(dateSortedDataMeasurements);
    setFinancialMeasurementRows(dateSortedFinancialMeasurements);

    const indexes = dateSortedDataMeasurements.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setSelection(indexes);

    const profitLossIndexes = dateSortedFinancialMeasurements.map((item, index) => (item.checked ? index : -1)).filter((index) => index !== -1);
    setProfitLossSelection(profitLossIndexes);

    // YAMAZUMI
    setGraphData(parseMeasurementRows(dateSortedDataMeasurements.filter((x) => x.checked)));

    // Measurements to display
    setMeasurementsToDisplay(dateSortedDataMeasurements.filter((x) => x.checked));

    // PROFIT & LOSS
    let tempProfitLossReportData;

    formatProfitLossReportData(
      dateSortedFinancialMeasurements.filter((x) => x.checked),
      currentProfitLossDates
    ).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
    });

    // TIMELINE
    var data = chartData(dateSortedDataMeasurements.filter((x) => x.checked).flat());
    if (JSON.parse(JSON.stringify(data)) !== JSON.parse(JSON.stringify(chartRows))) {
      setChartRows(data);
    }

    // FINANCIAL
    let financialChartMeasurements = filteredFinancialMeasurements
      .sort((a, b) => new Date(a.insertedOn) - new Date(b.insertedOn))
      .filter((x) => x.checked)
      .filter((x) => roundToSecond(new Date(x.insertedOn)) > new Date(currentDateRange.fromDate) && roundToSecond(new Date(x.insertedOn)) <= new Date(currentDateRange.toDate));

    let financialData = constructFinancialData(graphDetails, financialChartMeasurements);
    setFinancialChartData(financialData);

    // price data
    constructPriceData(graphProjects, currentDateRange.fromDate, currentDateRange.toDate);

    let tempGraphProjects = [...graphProjects]; // Clone the projects array
    let fromDate = currentDateRange.fromDate;
    let toDate = currentDateRange.toDate;

    tempGraphProjects = tempGraphProjects.map((project) => {
      return {
        ...project,
        details: project.details.map((detail) => {
          return {
            ...detail,
            measurements: detail.measurements.filter((measurement) => roundToSecond(new Date(measurement.insertedOn)) > fromDate && roundToSecond(new Date(measurement.insertedOn)) <= toDate),
          };
        }),
      };
    });
    setHighLowGraphProjects(tempGraphProjects);
  };

  const calculateProjection = (measurement, projection) => {
    let projectedValue = 0;

    if (projection?.priceDefinitions?.length > 0 && measurement.priceBundleId) {
      let bundle = null;
      for (const company of allPriceBundles) {
        const found = company.priceBundles.find((b) => b.id === measurement.priceBundleId);
        if (found) {
          bundle = found;
          break;
        }
      }

      if (bundle && bundle.items.length > 0) {
        // Fixed items are applied once per measurement while hourly items scale with
        // the stopwatch duration, so items are weighted by qty before computing the ratio.
        const qty = getMeasurementQty(measurement);
        const originalTotal = bundle.items.reduce((sum, item) => sum + item.price * item.multiplier * (item.isFixed ? 1 : qty), 0);
        const projectedTotal = bundle.items.reduce((sum, item) => {
          const pdProjection = projection.priceDefinitions.find(
            (pd) => pd.priceDefinitionId === item.priceDefinitionId
          );
          const price = pdProjection ? pdProjection.newPrice : item.price;
          return sum + price * item.multiplier * (item.isFixed ? 1 : qty);
        }, 0);

        if (originalTotal > 0) {
          projectedValue = measurement.eventPrice * (projectedTotal / originalTotal);
        } else {
          projectedValue = measurement.eventPrice;
        }
      } else {
        projectedValue = measurement.eventPrice;
      }
    } else if (projection?.accounts?.some((x) => x.id === measurement.accountId)) {
      projectedValue = (measurement.eventPrice * projection?.accounts?.find((x) => x.id === measurement.accountId)?.percentage) / 100;
    } else {
      projectedValue = measurement.eventPrice;
    }
    return projectedValue;
  };

  const limitString = (str, limit) => {
    if (str.length > limit) {
      return str.substring(0, limit) + "...";
    } else {
      return str;
    }
  };

  // Detail names for financial view
  const constructDetailCollection = (details) => {
    let temporaryCollection = [];
    for (let i = 0; i < details.length; i++) {
      let thisProjectDetail = details[i];
      if (thisProjectDetail.price > 0) {
        temporaryCollection.push({ id: thisProjectDetail.id, name: thisProjectDetail.description, registrationPoint: thisProjectDetail.resourceName, color: thisProjectDetail.color });
      }
    }
    //Empty array for all details, also add a total to display in the graph
    temporaryCollection.push({ id: -1, name: "Total", color: "#c7c9c8" });

    return temporaryCollection;
  };

  // Data for financial view
  const constructFinancialData = (projectDetails, chartDataCollection) => {
    let tempTotalData = [];
    let total = 0;
    let startTime = chartDataCollection.length > 0 ? chartDataCollection[0].insertedOn : new Date();
    let count = 0;

    //First we loop through all measurements to calculate the TOTAL LINE
    chartDataCollection.forEach((thisMeasurement) => {
      let isStopwatch = false;
      let isCount = false;
      let thisDetail = projectDetails.find((x) => x.id === thisMeasurement.detailId);
      if (thisDetail !== undefined && thisDetail.price > 0) {
        if (thisDetail.controlTypeId === 2 || (thisDetail.controlTypeId === 7 && thisDetail.externalApiTypeId === 2)) {
          isStopwatch = true;
        } else if (thisDetail.controlTypeId === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId == externalApiTypes.NUMERIC) {
          isCount = true;
        }

        let price_per = thisDetail.price !== null ? thisDetail.price : 0;
        let tempMeasurementData = {};
        var dif;
        var priceToAdd;
        if (isStopwatch) {
          let startDate = new Date(thisMeasurement.startTime);
          let stopDate = new Date(thisMeasurement.stopTime);
          if (stopDate > startDate) {
            dif = (stopDate.getTime() - startDate.getTime()) / 1000;
          } else {
            dif = 0;
          }
          priceToAdd = (thisDetail.price / 3600) * dif;
        } else if (isCount) {
          priceToAdd = thisMeasurement.numericValue * price_per;
        } else {
          priceToAdd = price_per;
        }
        if (thisDetail.isProfit === thisMeasurement.isNegative) {
          total -= priceToAdd;
        } else {
          total += priceToAdd;
        }
        if (count === 0) {
          tempMeasurementData = {
            id: thisMeasurement.id,
            measurement: "Total",
            date: startTime,
            price: 0,
            profitAdded: 0,
            isVisible: true,
            "-1_Total_price": 0,
            color: thisDetail.color,
            isProfit: thisDetail.isProfit,
            isNegative: thisMeasurement.isNegative,
            originalMeasurement: thisDetail.description,
          };
          tempTotalData.push(tempMeasurementData);
        }
        tempMeasurementData = {
          id: thisMeasurement.id,
          measurement: "Total",
          date: thisMeasurement.insertedOn,
          price: priceToAdd,
          profitAdded: priceToAdd,
          isVisible: true,
          "-1_Total_price": total,
          color: thisDetail.color,
          isProfit: thisDetail.isProfit,
          isNegative: thisMeasurement.isNegative,
          originalMeasurement: thisDetail.description,
        };
        tempTotalData.push(tempMeasurementData);
        count++;
      }
    });

    let tempDetailData = [];

    //Loop through the details and their measurements to get each unique detail line
    projectDetails
      .filter((x) => x.price > 0)
      .forEach((thisDetail) => {
        let isStopwatch = false;
        let isCount = false;
        let detailCount = 0;

        if (thisDetail.controlTypeId === 2 || (thisDetail.controlTypeId === 7 && thisDetail.externalApiTypeId === 2)) {
          //Stopwatch
          isStopwatch = true;
        } else if (thisDetail.controlTypeId === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId == externalApiTypes.NUMERIC) {
          isCount = true;
        }
        let thisMeasurements = chartDataCollection.filter((x) => x.detailId === thisDetail.id);

        let total = 0;
        let price_per = thisDetail.price !== null ? thisDetail.price : 0;
        let startTime = chartDataCollection.length > 0 ? chartDataCollection[0].insertedOn : new Date();

        if (thisMeasurements.length > 0) {
          let variablename = thisDetail.id + "_" + thisDetail.description + "_price";
          thisMeasurements.forEach((thisMeasurement) => {
            let tempMeasurementData = {};
            var dif;
            var priceToAdd;
            let eventPrice = 0;
            if (isStopwatch) {
              let startDate = new Date(thisMeasurement.startTime);
              let stopDate = new Date(thisMeasurement.stopTime);
              if (stopDate > startDate) {
                dif = (stopDate.getTime() - startDate.getTime()) / 1000;
              } else {
                dif = 0;
              }
              // eventPrice = (thisDetail.price / 3600) * dif;
              priceToAdd = (thisDetail.price / 3600) * dif;
            } else if (isCount) {
              eventPrice = thisMeasurement.numericValue * price_per;
              priceToAdd = thisMeasurement.numericValue * price_per;
            } else {
              eventPrice = price_per;
              priceToAdd = price_per;
            }
            if (thisDetail.isProfit === thisMeasurement.isNegative) {
              total -= priceToAdd;
            } else {
              total += priceToAdd;
            }
            if (detailCount === 0) {
              tempMeasurementData = {
                detailCount: 0,
                id: thisMeasurement.id,
                measurement: thisDetail.description,
                date: startTime,
                price: 0,
                profitAdded: 0,
                isVisible: true,
                [variablename]: 0,
                color: thisDetail.color,
                isProfit: thisDetail.isProfit,
                isNegative: thisMeasurement.isNegative,
              };
              tempDetailData.push(tempMeasurementData);
            }
            tempMeasurementData = {
              id: thisMeasurement.id,
              measurement: thisDetail.description,
              date: thisMeasurement.insertedOn,
              price: eventPrice,
              profitAdded: priceToAdd,
              isVisible: true,
              [variablename]: total,
              color: thisDetail.color,
              isProfit: thisDetail.isProfit,
              isNegative: thisMeasurement.isNegative,
            };
            tempDetailData.push(tempMeasurementData);
            detailCount++;
          });
        }
      });
    let combinedData = tempTotalData.concat(tempDetailData);
    return combinedData;
  };

  // TIMELINE
  const chartData = (data) => {
    let tempdata = [];
    let tempObj = {};

    data.forEach((x) => {
      let startDate = new Date(x.startTime);
      let tempEndDate = new Date(x.startTime);
      let insertedOn = new Date(x.startTime);
      let updatedOn = new Date(x.startTime);

      if (x.typeId === controlTypes.STOPWATCH || (x.typeId === controlTypes.EXTERNALAPI && x.externalApiTypeId === externalApiTypes.STOPWATCH)) {
        if (x.updatedOn !== undefined) {
          updatedOn = x.updatedOn;
        }
        if (x.stopTime !== null && x.stopTime !== undefined && x.stopTime.length > 0) {
          tempEndDate = new Date(x.stopTime);
        }
        if (x.insertedOn !== undefined) {
          insertedOn = x.insertedOn;
        }
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Stopwatch",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.dateValue,
          key: x.keyValue,
          counter: null,
          insertedOn: insertedOn,
          updatedOn: updatedOn,
          description: limitString(x.description, 15),
          color: x.registrationPointColor,
          chartType: "rangeBar",
          argumentField: "description",
          detailId: x.detailId,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.COUNTER) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Counter",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.integerValue,
          key: x.keyValue,
          counter: x.measurementValue,
          counterDescription: limitString(x.description, 15),
          color: x.registrationPointColor,
          chartType: "scatter",
          argumentField: "counterDescription",
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.FREETEXT) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Free text",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          value: x.textValue,
          key: x.keyValue,
          counter: null,
          freetextDescription: limitString(x.description, 15),
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.DATALIST) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Data list",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          datalistDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.CAMERA) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Image",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          image: x.imageId,
          thumbnail: x.thumbnail,
          cameraDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.SCANNER) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "Data list",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          datalistDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      } else if (x.typeId === controlTypes.EXTERNALAPI) {
        tempObj = {
          commentText: x.commentText,
          externalApiTypeId: x.externalApiTypeId,
          commentImage: x.commentImage,
          checked: x.checked,
          id: x.id,
          startDate: startDate,
          endDate: tempEndDate,
          companyName: x.companyName,
          measurementValue: x.measurementValue,
          type: "External API",
          projectName: x.projectName,
          projectId: x.projectId,
          resource: x.resourceName,
          typeId: x.typeId,
          key: x.keyValue,
          value: x.externalApiValues,
          externalApiDescription: limitString(x.description, 15),
          counter: null,
          color: x.registrationPointColor,
          linearTimeline: "",
        };
      }

      tempdata.push(tempObj);
    });
    tempdata = tempdata.filter((x) => x.checked);
    return tempdata;
  };

  // FINANCIAL - LOGIC
  const handleDownloadImage = async (toDownload) => {
    let element;
    let fileName;
    if (toDownload === "HighLow") {
      element = highLowRef.current;
      fileName = "HighLow.jpg";
    } else {
      element = summaryRef.current;
      fileName = "Summary.jpg";
    }
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  // Data to display in financialSummaryTable
  const constructPriceData = (projects) => {
    let aggregatedPriceData = [];
    let total = 0;
    if (projects.length > 0) {
      projects.forEach((project) => {
        let projectDetails = project.details;

        for (let i = 0; i < projectDetails.length; i++) {
          let thisDetail = projectDetails[i];
          let filteredMeasurements = thisDetail.measurements;
          let detailType = thisDetail.controlTypeId;

          // for each measurement, set filteredMeasurement.externalApiTypeId = thisDetail.externalApiTypeId
          filteredMeasurements.forEach((measurement) => {
            measurement.controlTypeId = thisDetail.controlTypeId;
            measurement.externalApiTypeId = thisDetail.externalApiTypeId;
          });

          if (filteredMeasurements.length > 0) {
            let totalTime = 0;
            let tempTotal = 0;
            let tempAmount = 0;

            if (detailType === controlTypes.STOPWATCH || (detailType === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId === externalApiTypes.STOPWATCH)) {
              for (let j = 0; j < filteredMeasurements.length; j++) {
                let startDate = new Date(filteredMeasurements[j].startValue);
                let stopDate = new Date(filteredMeasurements[j].stopValue);
                let dif = stopDate > startDate ? (stopDate.getTime() - startDate.getTime()) / 1000 : 0;
                totalTime += dif;
              }
              tempTotal = (thisDetail.price / 3600) * totalTime;
              tempAmount = totalTime;
            } else if (detailType === controlTypes.EXTERNALAPI && thisDetail.externalApiTypeId === externalApiTypes.NUMERIC) {
              let totalCount = filteredMeasurements.reduce((acc, x) => {
                if (x.isNegative) {
                  return acc - x.numericValue;
                } else {
                  return acc + x.numericValue;
                }
              }, 0);
              tempTotal = totalCount * thisDetail.price;
              tempAmount = totalCount;
            } else if (detailType === controlTypes.COUNTER) {
              let positiveMeasurements = filteredMeasurements.filter((x) => x.isNegative === false);
              let negativeMeasurements = filteredMeasurements.filter((x) => x.isNegative === true);

              let positiveTotal = positiveMeasurements.reduce((acc, x) => acc + x.price, 0);
              let negativeTotal = negativeMeasurements.reduce((acc, x) => acc + x.price, 0);

              tempTotal = positiveTotal - negativeTotal;
              tempAmount = positiveMeasurements.length - negativeMeasurements.length;
            } else {
              tempTotal = filteredMeasurements.length * (thisDetail.price !== null ? thisDetail.price : 0);
              tempAmount = filteredMeasurements.length;
            }

            let lowestDate = filteredMeasurements.reduce((minDate, measurement) => {
              let insertedOn = new Date(measurement.insertedOn);
              return insertedOn < minDate ? insertedOn : minDate;
            }, new Date(filteredMeasurements[0].insertedOn));

            let highestDate = filteredMeasurements.reduce((maxDate, measurement) => {
              let insertedOn = new Date(measurement.insertedOn);
              return insertedOn > maxDate ? insertedOn : maxDate;
            }, new Date(filteredMeasurements[0].insertedOn));

            aggregatedPriceData.push({
              projectId: project.id,
              projectName: project.projectName,
              resourceName: filteredMeasurements[0].resourceName,
              price: thisDetail.price !== null ? thisDetail.price : 0,
              totalPrice: tempTotal,
              isProfit: thisDetail.isProfit !== null ? thisDetail.isProfit : true,
              detailDescription: thisDetail.description,
              first: lowestDate,
              last: highestDate,
              amount: tempAmount,
              color: thisDetail.color,
              type: detailType,
              externalApiTypeId: filteredMeasurements[0].externalApiTypeId,
              minuteamount: !isStopwatch(filteredMeasurements[0]) ? null : convertHMS(totalTime),
            });

            if (thisDetail.isProfit) {
              total += tempTotal;
            } else {
              total -= tempTotal;
            }
          }
        }
      });

      aggregatedPriceData = aggregatedPriceData.filter((x) => x.price > 0);

      setTotalPrice(total);
      setPriceData(aggregatedPriceData);
    }
  };

  const isStopwatch = (measurement) => {
    let measurementControlTypeId = measurement.controlTypeId || measurement.type;
    let measurementExternalApiTypeId = measurement.externalApiTypeId;

    if (measurementControlTypeId === controlTypes.STOPWATCH || (measurementControlTypeId === controlTypes.EXTERNALAPI && measurementExternalApiTypeId === externalApiTypes.STOPWATCH)) {
      return true;
    } else {
      return false;
    }
  };

  const getControlTypeIcon = (typeId) => {
    let icon;
    switch (typeId) {
      case controlTypes.COUNTER:
        icon = <DiamondPlus className={classes.icon} />;
        break;
      case controlTypes.STOPWATCH:
        icon = <Timer className={classes.icon} />;
        break;
      case controlTypes.FREETEXT:
        icon = <TextCursorInput className={classes.icon} />;
        break;
      case controlTypes.DATALIST:
        icon = <List className={classes.icon} />;
        break;
      case controlTypes.SCANNER:
        icon = <ScanBarcode className={classes.icon} />;
        break;
      case controlTypes.CAMERA:
        icon = <Image className={classes.icon} />;
        break;
      case controlTypes.EXTERNALAPI:
        icon = <LinkIcon className={classes.icon} />;
        break;
      default:
      // code block
    }
    return icon;
  };

  const exportProfitLossReport = () => {
    if (profitLossReportComponentRef.current) {
      profitLossReportComponentRef.current.downloadReport(); // Call the function
    }
  };

  const comparePeriod = () => {
    // Setting true, check if there is already a compare date, if not --> Take the same length as the first daterange.
    const index = Object.keys(profitLossDates).length;

    if (index === 1) {
      setPercentageVisible(true);
    }

    if (compareLimit >= index) {
      var tempProfitLossDates = { ...profitLossDates };

      const [lastKey, lastValue] = Object.entries(tempProfitLossDates).at(0);

      var existingEndDate = lastValue.endDate;
      var existingStartDate = lastValue.startDate;

      const differenceInMs = existingEndDate - existingStartDate;
      const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);

      var newEndDate = new Date(lastValue.startDate.getTime() - 1000);
      var newStartDate = new Date(lastValue.startDate - differenceInMs);

      // for each tempProfitLossDate, set sequence to current sequence + 1
      tempProfitLossDates = Object.values(tempProfitLossDates).map((x) => {
        return { ...x, sequence: x.sequence + 1 };
      });

      tempProfitLossDates[index] = { fromDate: new Date(newStartDate), startDate: new Date(newStartDate), toDate: new Date(newEndDate), endDate: new Date(newEndDate), sequence: 1 };

      setProfitLossDates(tempProfitLossDates);

      // get earliest fromDate and startDate from profitLossDates, and latest toDate and endDate then set to timeRangeDates
      let earliestFromDate = new Date(
        Math.min.apply(
          null,
          Object.values(tempProfitLossDates).map((x) => x.fromDate)
        )
      );
      let latestToDate = new Date(
        Math.max.apply(
          null,
          Object.values(tempProfitLossDates).map((x) => x.toDate)
        )
      );

      let tempTimeRangeDates = { fromDate: earliestFromDate, toDate: latestToDate, startDate: earliestFromDate, endDate: latestToDate };
      setTimeRangeDates(tempTimeRangeDates);

      let tempProfitLossReportData;
      formatProfitLossReportData(originalMeasurementRows, tempProfitLossDates).then((formattedData) => {
        tempProfitLossReportData = formattedData;
        setProfitLossReportData(tempProfitLossReportData);
        setBoolCompareDate(true);
      });

      onFiltersChange(financialFilters, tempTimeRangeDates, tempProfitLossDates);
    } else {
      toast.error(t('modifyGraph.compareLimitReached'), { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
    }
  };

  const resetComparePeriod = () => {
    // remove all dates except the last one and change its sequence to 1
    let tempProfitLossDates = { ...profitLossDates };
    let lastKey = Object.keys(tempProfitLossDates).pop();
    let lastValue = tempProfitLossDates[lastKey];
    tempProfitLossDates = { [lastKey]: lastValue };

    // for each tempProfitLossDate, set sequence to 1
    tempProfitLossDates = Object.values(tempProfitLossDates).map((x) => {
      return { ...x, sequence: 1 };
    });

    setProfitLossDates(tempProfitLossDates);

    // get earliest fromDate and startDate from profitLossDates, and latest toDate and endDate then set to timeRangeDates
    let earliestFromDate = new Date(
      Math.min.apply(
        null,
        Object.values(tempProfitLossDates).map((x) => x.fromDate)
      )
    );
    let latestToDate = new Date(
      Math.max.apply(
        null,
        Object.values(tempProfitLossDates).map((x) => x.toDate)
      )
    );

    let tempTimeRangeDates = { fromDate: earliestFromDate, toDate: latestToDate, startDate: earliestFromDate, endDate: latestToDate };
    setTimeRangeDates(tempTimeRangeDates);

    let tempProfitLossReportData;
    formatProfitLossReportData(originalMeasurementRows, tempProfitLossDates).then((formattedData) => {
      tempProfitLossReportData = formattedData;
      setProfitLossReportData(tempProfitLossReportData);
      setBoolCompareDate(false);
    });

    onFiltersChange(financialFilters, tempTimeRangeDates, tempProfitLossDates);
    setPercentageVisible(false);
  };

  const setCompareDateRange = (index, startDate, endDate, sequence) => {
    let varFilteredResults = JSON.parse(JSON.stringify(originalMeasurementRows)).sort((a, b) => new Date(b.insertedOn) - new Date(a.insertedOn)); // Clone original rows

    if (startDate && endDate) {
      var tempProfitLossDates = { ...profitLossDates };
      tempProfitLossDates[index] = { sequence: sequence, fromDate: new Date(startDate), startDate: new Date(startDate), toDate: new Date(endDate), endDate: new Date(endDate) };
      let tempProfitLossReportData;
      formatProfitLossReportData(
        varFilteredResults.filter((x) => x.checked),
        tempProfitLossDates
      ).then((formattedData) => {
        tempProfitLossReportData = formattedData;
        setProfitLossReportData(tempProfitLossReportData);
        setProfitLossDates(tempProfitLossDates);
      });
    } else {
      resetCompareDateRangeFilter();
    }
  };

  const resetCompareDateRangeFilter = () => {};

  const isFinancialView = () => {
    return selectedGraphType === graphTypes.FINANCIAL || selectedGraphType === graphTypes.PROFITLOSS;
  };

  const isInsightsView = () => {
    return selectedGraphType === graphTypes.INSIGHTS;
  };

  const isChartTabView = () =>
    selectedGraphType === graphTypes.OUTPUT ||
    selectedGraphType === graphTypes.YAMAZUMI ||
    selectedGraphType === graphTypes.TIMELINE;

  const isProfitLossTabView = () => selectedGraphType === graphTypes.PROFITLOSS;
  const isFinancialTabView = () => selectedGraphType === graphTypes.FINANCIAL;

  const getProfitLossDisplayData = (serverData) => {
    if (!serverData) return null;
    const toIdx = (val) => ({ 0: val });
    const mapAccounts = (accounts) =>
      (accounts || []).map((a) => ({ id: a.accountId, name: a.accountName, isActive: true, total: toIdx(a.total), percentage: {} }));
    const mapCategories = (cats) =>
      (cats || []).map((c) => ({
        id: c.categoryId, name: c.categoryName, isRevenue: c.isRevenue,
        accountResults: mapAccounts(c.accounts),
        total: toIdx(c.total), percentage: {},
      }));
    return {
      companies: serverData.companies || [],
      revenueAccountCategoryResults: mapCategories(serverData.revenueCategories),
      costAccountCategoryResults: mapCategories(serverData.costCategories),
      totalProfit: toIdx(serverData.totalProfit),
      revenueTotal: toIdx(serverData.revenueTotal),
      costTotal: toIdx(serverData.costTotal),
      percentageTotalProfit: {},
    };
  };

  const mergeRecurringIntoServerPLD = (serverPLD, items) => {
    if (!serverPLD || !items?.length) return serverPLD;
    const rangeIndex = 0;
    const result = {
      ...serverPLD,
      companies: [...serverPLD.companies],
      revenueAccountCategoryResults: serverPLD.revenueAccountCategoryResults.map(cat => ({
        ...cat, total: { ...cat.total }, percentage: { ...cat.percentage },
        accountResults: cat.accountResults.map(acc => ({ ...acc, total: { ...acc.total }, percentage: { ...acc.percentage } })),
      })),
      costAccountCategoryResults: serverPLD.costAccountCategoryResults.map(cat => ({
        ...cat, total: { ...cat.total }, percentage: { ...cat.percentage },
        accountResults: cat.accountResults.map(acc => ({ ...acc, total: { ...acc.total }, percentage: { ...acc.percentage } })),
      })),
      totalProfit: { ...serverPLD.totalProfit },
      revenueTotal: { ...serverPLD.revenueTotal },
      costTotal: { ...serverPLD.costTotal },
    };
    items.forEach(m => {
      const amount = m.isNegative ? -m.eventPrice : m.eventPrice;
      const isRevenue = m.isProfit;
      const categoryList = isRevenue ? result.revenueAccountCategoryResults : result.costAccountCategoryResults;
      if (m.companyName && !result.companies.includes(m.companyName)) {
        result.companies.push(m.companyName);
        result.companies.sort((a, b) => a.localeCompare(b));
      }
      let category = categoryList.find(c => c.id === m.accountCategoryId);
      if (!category) {
        category = { id: m.accountCategoryId, name: m.accountCategoryName, isRevenue, accountResults: [], total: { [rangeIndex]: 0 }, percentage: {} };
        categoryList.push(category);
      }
      let account = category.accountResults.find(a => a.id === m.accountId);
      if (!account) {
        account = { id: m.accountId, name: m.accountName, isActive: true, total: { [rangeIndex]: 0 }, percentage: {} };
        category.accountResults.push(account);
      }
      account.total[rangeIndex] = (account.total[rangeIndex] || 0) + amount;
      category.total[rangeIndex] = (category.total[rangeIndex] || 0) + amount;
      if (isRevenue) {
        result.revenueTotal[rangeIndex] = (result.revenueTotal[rangeIndex] || 0) + amount;
      } else {
        result.costTotal[rangeIndex] = (result.costTotal[rangeIndex] || 0) + amount;
      }
      result.totalProfit[rangeIndex] = (result.totalProfit[rangeIndex] || 0) + (isRevenue ? amount : -amount);
    });
    return result;
  };

  const insightsMeasurements = useMemo(() => {
    if (!activeProjection) {
      return graphInsights?.rangeStart ? [] : measurementsToDisplay;
    }
    return originalMeasurementRows.map((m) => ({
      ...m,
      projectedEventPrice: calculateProjection(m, activeProjection),
    }));
  }, [activeProjection, originalMeasurementRows, allPriceBundles, graphInsights, measurementsToDisplay]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetActiveProjection = (projection) => {
    if (projection === null) {
      onFiltersChange(null, timeRangeDates, profitLossDates, "reset");
    } else {
      onFiltersChange(null, timeRangeDates, profitLossDates, projection);
    }
    setActiveProjection(projection);
  };

  return (
    <React.Fragment>
      {loading && !(selectedGraphType === graphTypes.SIMULATIONS) && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
        </div>
      )}
      {noGraphFound ? (
        <div className={classes.noGraphContainer}>
          <p className={classes.noGraphContainer__text}>{t('modifyGraph.noGraphFound')}</p>
        </div>
      ) : (
        <Box className="page-container">
          <div className={classes.container}>
            <div className={classes.header__titleRow}>
              <div className={classes.header__titleContainer}>
                <div className="page-title-stack">
                  <Breadcrumb onClick={() => navigate("/graphs")}>{t('nav.graphsOverview')}</Breadcrumb>
                  <HeaderTitle>{create ? t('modifyGraph.createTitle') : t('modifyGraph.modifyTitle')}</HeaderTitle>
                </div>

                <div className={classes.buttonContainer}>
                  {!create && (
                    <ShareGraphDropdown
                      isPublished={graphIsPublished}
                      shareUrl={getShareUrl()}
                      onEnable={handleEnableSharing}
                      onDisable={handleDisableSharing}
                      loading={sharingLoading}
                    />
                  )}
                  <Button onClick={() => saveGraphConfiguration()}>
                    {create ? t('modifyGraph.saveGraph') : t('modifyGraph.saveChanges')}
                  </Button>
                </div>
              </div>
              <GraphTabs
                selectedGraphType={selectedGraphType}
                handleGraphTypeSelection={handleGraphTypeSelection}
                accountCategories={usedAccountCategories}
                activeProjection={activeProjection}
                handleSetActiveProjection={handleSetActiveProjection}
                projectionIsActive={!!activeProjection}
                projectionCurrency={activeCurrency}
                projectionName={activeProjection?.name}
                {...props}
              />
            </div>
            <div className={classes.projectSelectionContainer}>
              <div className={classes.formContainer}>
                <div className={classes.formColumn}>
                  <div className={classes.projectOption}>
                    <div className={classes.projectOption__labelContainer}>
                      {graphConfigurationNameError === "" ? (
                        <p className={classes.labelContainer__label}>{t('modifyGraph.graphName')}</p>
                      ) : (
                        <p className={classes.labelContainer__helperText}>{graphConfigurationNameError}</p>
                      )}
                    </div>
                    <div className={classes.projectOption__inputContainer}>
                      <Input
                        name="graphConfigurationName"
                        value={graphConfigurationName}
                        onChange={setName}
                        placeholder={t('common.name')}
                        error={graphConfigurationNameError !== ""}
                        size="large"
                        fullwidth
                      />
                    </div>
                  </div>
                  <div className={classes.projectOption}>
                    <div className={classes.projectOption__labelContainer}>
                      {graphConfigurationCompanyIdError === "" ? (
                        <p className={classes.labelContainer__label}>{t('modifyGraph.selectCompany')}</p>
                      ) : (
                        <p className={classes.labelContainer__helperText}>{graphConfigurationCompanyIdError}</p>
                      )}
                    </div>
                    <div className={classes.projectOption__inputContainer}>
                      <CustomSelect
                        value={graphConfigurationCompanyId}
                        onChange={(val) => setGraphConfigurationCompanyId(val)}
                        error={graphConfigurationCompanyIdError !== ""}
                        searchable
                        placeholder={t('user.company')}
                        size="large"
                        fullwidth
                      >
                        <SelectTrigger>{allUserCompanies.find((c) => c.id === graphConfigurationCompanyId)?.name}</SelectTrigger>
                        <SelectContent>
                          {allUserCompanies.length < 1 && (
                            <SelectItem value="" disabled>
                              <em>{t('modifyGraph.notPartOfCompany')}</em>
                            </SelectItem>
                          )}
                          {allUserCompanies.map((company, index) => (
                            <SelectItem key={index} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </CustomSelect>
                    </div>
                  </div>
                </div>
                <div className={classes.listDescriptionStyle}>
                  <div className={classes.projectOption__labelContainer}>
                    <p className={classes.labelContainer__label}>{t('modifyGraph.graphDescription')}</p>
                  </div>
                  <textarea className={classes.description__textarea} value={graphConfigurationDescription} onChange={(e) => setDescription(e)}></textarea>
                </div>
              </div>
            </div>

            <div className={classes.graphControlsRow}>
              <CompactDateRangePicker
                startDate={timeRangeDates.fromDate}
                endDate={timeRangeDates.endDate}
                onChange={setDateRange}
              />
              {(selectedGraphType === graphTypes.OUTPUT ||
                selectedGraphType === graphTypes.YAMAZUMI ||
                selectedGraphType === graphTypes.TIMELINE) && (
                <>
                  <TimeRangeSelector value={timeRangeZoom} onChange={handleTimeRangeZoomChange} size="medium" />
                  <TimeRangeScroll
                    startDate={viewRangeDates.fromDate}
                    endDate={viewRangeDates.endDate}
                    onPrev={handleTimeRangeScrollPrev}
                    onNext={handleTimeRangeScrollNext}
                    minDate={timeRangeDates.fromDate}
                    maxDate={timeRangeDates.toDate}
                  />
                </>
              )}
              <ProjectOrderBar
                projects={orderedProjectNames}
                onChange={(newOrder) => {
                  setOrderedProjectNames(newOrder);
                  setGraphConfigurationData((prev) => {
                    const updatedProjects = prev.projects.map((p) => {
                      const project = graphProjects.find((gp) => gp.id === p.projectId);
                      const idx = newOrder.indexOf(project?.resourceDescription);
                      return { ...p, sequence: idx !== -1 ? idx + 1 : p.sequence };
                    });
                    return { ...prev, projects: updatedProjects };
                  });
                }}
                selectedProjectObjects={orderedProjectNames
                  .map((name) => selectedProjects.find((p) => p.resourceDescription === name))
                  .filter(Boolean)
                  .map((p) => ({ id: p.id, name: p.resourceDescription }))}
                allProjects={allProjects.flatMap((company) =>
                  company.projects
                    .filter(
                      (p) =>
                        !selectedProjects.some((sel) => sel.id === p.id) &&
                        !(activeCurrency && p.currencyId !== activeCurrency.id)
                    )
                    .map((p) => ({ id: p.id, projectName: p.projectName, resourceDescription: p.resourceDescription }))
                )}
                onAdd={(projectId) => {
                  for (const company of allProjects) {
                    const project = company.projects.find((p) => p.id === projectId);
                    if (project) { handleProjectSelection(project); break; }
                  }
                }}
                onRemove={removeSelectedProject}
              />
            </div>
            <div className={classes.chartContainer}>
              {(selectedGraphType === graphTypes.OUTPUT || selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.PROFITLOSS || selectedGraphType === graphTypes.INSIGHTS) && (
                <div className={classes.chartContainer__buttonContainer}>
                  <div className={classes.buttonContainer__start}>
                    {selectedGraphType === graphTypes.PROFITLOSS && (
                      <>
                        <BoolToggle
                          size="large"
                          label={t('modifyGraph.displayPercentages')}
                          disabled={Object.keys(profitLossDates).length <= 1 && !activeProjection}
                          onChange={setPercentageVisible}
                          boolValue={percentageVisible}
                        />
                        <BoolToggle
                          size="large"
                          label={t('modifyGraph.includeRecurringPrices')}
                          disabled={false}
                          onChange={setShowRecurringPrices}
                          boolValue={showRecurringPrices}
                        />
                      </>
                    )}
                    {(selectedGraphType === graphTypes.YAMAZUMI || selectedGraphType === graphTypes.OUTPUT) && (
                      <BoolToggle disabled={false} size="large" label={t('modifyGraph.displayLabels')} onChange={setLabelVisible} boolValue={labelVisible} />
                    )}
                  </div>
                  <div className={classes.buttonContainer__end}>
                    {selectedGraphType === graphTypes.PROFITLOSS && (
                      <Button variant="outlined" onClick={exportProfitLossReport}>
                        {t('modifyGraph.exportPdf')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {selectedGraphType === graphTypes.FINANCIAL && (
                <div className={classes.timeRangePickerContainer}>
                  <div className={classes.filterBlock}>
                    {/* Functionality disabled for now */}
                    {/* {selectedGraphType === graphTypes.TIMELINE && <BoolToggle disabled={false} size="large" label="Display linear" onChange={setShowLinearTimeline} boolValue={showLinearTimeline} />} */}
                    <DateRangeSlider updateDataRange={updateDataRange} timeRangeDates={timeRangeDates}></DateRangeSlider>
                  </div>
                </div>
              )}
              <div className={classes.yamazumiContainer}>
                {selectedGraphType === graphTypes.INSIGHTS && (
                  <InsightsTab
                    allMeasurements={insightsMeasurements}
                    graphProjects={graphProjects}
                    currency={activeCurrency}
                    loading={fetchingData}
                    timeRangeDates={timeRangeDates}
                    activeProjection={activeProjection}
                    serverInsights={graphInsights}
                  />
                )}
                {selectedGraphType === graphTypes.SIMULATIONS && (
                  <ProjectionsTab
                    graphId={graphId}
                    profitLossReportData={getProfitLossDisplayData(serverProfitLossData) || profitLossReportData}
                    allPriceBundles={allPriceBundles}
                    allMeasurements={originalMeasurementRows}
                    currency={activeCurrency}
                    login={props.Login}
                    activeProjection={activeProjection}
                    handleSetActiveProjection={handleSetActiveProjection}
                    selectedScenarioId={simulationActiveScenarioId}
                    onSelectedScenarioChange={setSimulationActiveScenarioId}
                    appliedScenarioId={simulationAppliedScenarioId}
                    onAppliedScenarioChange={setSimulationAppliedScenarioId}
                  />
                )}
                {selectedGraphType === graphTypes.OUTPUT && (
                  <>
                    <OutputChart
                      {...props}
                      allMeasurements={viewFilteredMeasurements}
                      currency={activeCurrency}
                      labelVisible={labelVisible}
                      graphConfigurationName={graphConfigurationName}
                      loading={fetchingData}
                      emptyState={viewFilteredMeasurements.length === 0}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.YAMAZUMI && (
                  <>
                    {(selectedProjects.length === 0 || graphData.length === 0 || dataMeasurementRows.length === 0) && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>{getEmptyStateTitle()}</p>
                          <p className={classes.emptyStateCard__subtitle}>{getEmptyStateSubtitle()}</p>
                        </div>
                      </div>
                    )}
                    <YamazumiChart
                      {...props}
                      allMeasurements={viewFilteredMeasurements}
                      currency={activeCurrency}
                      dataSource={graphData}
                      labelVisible={labelVisible}
                      graphConfigurationName={graphConfigurationName}
                      emptyState={viewFilteredMeasurements.length === 0}
                      loading={fetchingData}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.TIMELINE && (
                  <>
                    {chartRows.filter((x) => {
                      const t = new Date(x.startDate).getTime();
                      if (isNaN(t)) return false;
                      return t >= viewRangeDates.fromDate.getTime() && t <= viewRangeDates.endDate.getTime();
                    }).length === 0 && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>{t('modifyGraph.noMeasurements')}</p>
                          <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noMeasurementsSubtitle')}</p>
                        </div>
                      </div>
                    )}
                    <TimeLineChart
                      data={chartRows.filter((x) => {
                        const t = new Date(x.startDate).getTime();
                        if (isNaN(t)) return false;
                        return t >= viewRangeDates.fromDate.getTime() && t <= viewRangeDates.endDate.getTime();
                      })}
                      visualisationType={showLinearTimeline ? timeLineVisualisationTypes.LINEAREVENTS : timeLineVisualisationTypes.SPLIT}
                      from={parseNewDateToDDMMYYYWithoutSeconds(viewRangeDates.fromDate)}
                      to={parseNewDateToDDMMYYYWithoutSeconds(viewRangeDates.endDate)}
                      loading={fetchingData}
                      onChartViewChange={setTimelineChartView}
                      registrationPoints={orderedProjectNames}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.FINANCIAL && (
                  <>
                    {financialChartData.length === 0 && (
                      <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateCard}>
                          <p className={classes.emptyStateCard__title}>{t('modifyGraph.noData')}</p>
                          <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noFinancialData')}</p>
                        </div>
                      </div>
                    )}
                    <FinancialChart
                      financialChartData={financialChartData}
                      projectDetailNames={projectDetailNames}
                      currency={activeCurrency}
                      from={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.fromDate)}
                      to={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.toDate)}
                    />
                  </>
                )}
                {selectedGraphType === graphTypes.PROFITLOSS && (() => {
                  const serverPLD = getProfitLossDisplayData(serverProfitLossData);
                  const activePLD = showRecurringPrices
                    ? (mergeRecurringIntoServerPLD(serverPLD, recurringFinancials) || profitLossReportData)
                    : (serverPLD || profitLossReportData);
                  return (
                    <>
                      {(!activePLD || (Array.isArray(activePLD) && activePLD.length === 0)) && (
                        <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateCard}>
                            <p className={classes.emptyStateCard__title}>{t('modifyGraph.noData')}</p>
                            <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noFinancialData')}</p>
                          </div>
                        </div>
                      )}
                      <ProfitLossReport
                        ref={profitLossReportComponentRef}
                        allMeasurements={originalMeasurementRows}
                        data={financialMeasurementRows}
                        profitLossReportData={activePLD}
                        from={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.fromDate)}
                        to={parseNewDateToDDMMYYYWithoutSeconds(timeRangeDates.toDate)}
                        boolCompareDate={boolCompareDate}
                        comparePeriod={comparePeriod}
                        resetComparePeriod={resetComparePeriod}
                        percentageVisible={percentageVisible}
                        compareLimit={compareLimit}
                        setCompareDateRange={setCompareDateRange}
                        profitLossDates={profitLossDates}
                        currency={activeCurrency}
                        projection={activeProjection}
                        allPriceBundles={allPriceBundles}
                        loading={fetchingData}
                      />
                    </>
                  );
                })()}
              </div>

              <div style={{ visibility: isFinancialView() || isInsightsView() || selectedGraphType === graphTypes.SIMULATIONS ? "hidden" : "visible", height: isFinancialView() || isInsightsView() || selectedGraphType === graphTypes.SIMULATIONS ? "0px" : "auto" }}>
                <DevExDataTable
                  data={selectedGraphType === graphTypes.YAMAZUMI ? filterByViewRange(yamazumiMeasurementRows) : selectedGraphType === graphTypes.OUTPUT ? filterByViewRange(outputMeasurementRows) : selectedGraphType === graphTypes.TIMELINE ? filterByViewRange(timelineChartView === "timeline" ? yamazumiMeasurementRows : timelineEventMeasurementRows) : dataMeasurementRows}
                  columns={resultColumns}
                  onSelectionChange={onSelectionChange}
                  selection={selection}
                  currentFilters={dataFilters}
                  handleFiltersChange={onFiltersChange}
                  currency={activeCurrency}
                  activeProjection={activeProjection}
                  allPriceBundles={allPriceBundles}
                  defaultHiddenColumnNames={["companyName", "projectName", "running", "action"]}
                  tableId="modify-graph-results"
                />
              </div>

              <div style={{ visibility: isFinancialView() && !isInsightsView() ? "visible" : "hidden", height: isFinancialView() && !isInsightsView() ? "auto" : "0px" }}>
                <ProfitLossDevExDataTable
                  data={financialMeasurementRows}
                  columns={profitLossResultColumns}
                  currency={activeCurrency}
                  onSelectionChange={onProfitLossSelectionChange}
                  selection={profitLossSelection}
                  currentFilters={financialFilters}
                  handleFiltersChange={onFiltersChange}
                  activeProjection={activeProjection}
                  allPriceBundles={allPriceBundles}
                  defaultHiddenColumnNames={["companyName", "projectName", "running", "action"]}
                  tableId="modify-graph-profitloss"
                />
              </div>
            </div>
            {selectedGraphType === graphTypes.FINANCIAL && (
              <div className={classes.financialDetailInformationContainer}>
                <div className={classes.highLowContainer} ref={highLowRef}>
                  <div className={classes.filterTitle}>
                    <span>{t('modifyGraph.highLow')}</span>
                    <Button variant="secondary" onClick={() => handleDownloadImage("HighLow")} data-html2canvas-ignore="true">
                      {t('common.export')}
                    </Button>
                  </div>

                  {loading ? (
                    <Box className={classes.loadingBalls}>
                      <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
                    </Box>
                  ) : highLowGraphProjects.length > 0 ? (
                    highLowGraphProjects.map((project, projectIndex) => (
                      <Box key={`GraphProject_${projectIndex}`} className={classes.highLow__projectContainer}>
                        {/* Project subtitle */}
                        <h3 className={classes.highLow__projectSubtitle}>{project.name}</h3>

                        {/* Details of each project */}
                        {project.details
                          .filter((x) => x.measurements.length > 0)
                          .map((detail, index) => {
                            if (detail.price > 0) {
                              return (
                                <div key={`TableHighLow_${detail.id}_${index}`} className={classes.highLow__detail}>
                                  <div className={classes.highLow__headerContainer}>
                                    <div className={classes.highLow__detailHeader}>
                                      {getControlTypeIcon(detail.controlTypeId)}
                                      <p className={classes.highLow__detailTitle}>{detail.description}</p>
                                    </div>
                                    <div className={classes.headerContainer__detailInformation}>
                                      <div className={classes.detailInformation__registrationPoint}>
                                        <Tooltip title={detail.measurements[0].resourceName} placement="top">
                                          <MapPin className={classes.registrationPoint__icon} />
                                        </Tooltip>
                                        {/* <p className={classes.registrationPoint__name}>{detail.measurements[0].resourceName}</p> */}
                                      </div>
                                      {detail.isProfit ? <Plus style={{ color: "rgb(40, 167, 69)", paddingRight: "10px" }} /> : <Minus style={{ color: "rgb(220, 53, 69)", paddingRight: "10px" }} />}
                                    </div>
                                  </div>
                                  <Divider />
                                  {detail.measurements.length > 0 ? (
                                    <TableHighLow
                                      {...props}
                                      detail={detail}
                                      projectName={project.projectName}
                                      location={project.resourceDescription}
                                      projectQr={project.qrCode}
                                      currency={activeCurrency}
                                    />
                                  ) : (
                                    <p className={classes.emptyStateCard__subtitle} style={{ paddingTop: "8px", paddingBottom: "8px" }}>
                                      {t('modifyGraph.noMeasurementsTimespan')}
                                    </p>
                                  )}
                                </div>
                              );
                            } else {
                              return <React.Fragment key={`EmptyDetail_${index}`}></React.Fragment>;
                            }
                          })}
                      </Box>
                    ))
                  ) : (
                    <></>
                  )}

                  {dataMeasurementRows.length === 0 && !loading && (
                    <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "center" }}>
                      <div className={classes.emptyStateCard}>
                        <p className={classes.emptyStateCard__title}>{t('modifyGraph.noEvents')}</p>
                        <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noEventsSubtitle')}</p>
                      </div>
                    </Box>
                  )}
                </div>
                <div className={classes.financialDetailInformationContainer__right}>
                  <div className={classes.pieChartsContainer}>
                    <div className={classes.pieStyle}>
                      {(!priceData || priceData.filter((x) => !isStopwatch(x)).length === 0) && (
                        <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateCard}>
                            <p className={classes.emptyStateCard__title}>{t('modifyGraph.noEvents')}</p>
                            <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noEventMeasurementsTimespan')}</p>
                          </div>
                        </div>
                      )}
                      <EventChart data={priceData.filter((x) => !isStopwatch(x) && x.price > 0)} title={t('modifyGraph.eventOverview')} />
                    </div>
                    <div className={classes.pieStyle}>
                      {priceData.filter((x) => isStopwatch(x)).length === 0 && (
                        <div className={classes.emptyStateContainer}>
                          <div className={classes.emptyStateCard}>
                            <p className={classes.emptyStateCard__title}>{t('modifyGraph.noStopwatches')}</p>
                            <p className={classes.emptyStateCard__subtitle}>{t('modifyGraph.noStopwatchMeasurementsTimespan')}</p>
                          </div>
                        </div>
                      )}
                      <EventChart data={priceData.filter((x) => isStopwatch(x))} title={t('modifyGraph.timeOverview')} />
                    </div>
                  </div>
                  <div className={classes.financialSummaryContainer} ref={summaryRef}>
                    <div className={classes.calculateBoxStyle}>
                      <div className={classes.filterTitle} variant="contained" content="Options">
                        <span>{t('modifyGraph.summary')}</span>
                        <div>
                          <Button variant="secondary" onClick={() => handleDownloadImage("Summary")} data-html2canvas-ignore="true">
                            {t('common.export')}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <FinancialSummaryTable totalPrice={totalPrice} priceData={priceData} currency={activeCurrency} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Box>
      )}
    </React.Fragment>
  );
}

const useStyles = makeStyles((theme) => ({
  noGraphContainer: {
    width: "400px",

    margin: "auto",
    marginTop: "125px",
    padding: "16px",

    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  noGraphContainer__text: {
    fontColor: "#0C0E29",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    margin: "0",
    textAlign: "center",
  },

  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    zIndex: 9999,
    position: "fixed",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },
  bar: {
    [theme.breakpoints.up("lg")]: {
      minWidth: "70%",
    },
    [theme.breakpoints.down("lg")]: {
      minWidth: "60%",
    },
    [theme.breakpoints.down("md")]: {
      minWidth: "65%",
    },
    [theme.breakpoints.down("sm")]: {
      minWidth: "70%",
    },
  },

  header__titleRow: {
    width: "100%",
    minHeight: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",

    marginBottom: "16px",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },

  header__titleContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  toggleFilter: {
    height: "32px",
    boxSizing: "border-box",

    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",

    [theme.breakpoints.down("sm")]: {
      position: "relative",
      left: "0",
      transform: "none",
    },
  },

  toggleFilter__optionButton: {
    height: "100%",
    width: "128px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },

    [theme.breakpoints.down("xs")]: {
      width: "96px",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(100% / 4)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  formContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "16px",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: "8px",
    },
  },

  formColumn: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  projectOption: {
    minWidth: "240px",
    maxWidth: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  projectSelectionContainer: {
    width: "100%",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    backgroundColor: "white",
    borderRadius: "12px",

    padding: "16px",
  },

  errorClass: {
    outline: "1px solid #D84949",
  },

  selectedProjectPill: {
    height: "28px",
    maxWidth: "100%",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",
    gap: "0",
    padding: "0 12px",
    paddingRight: "2px",
    borderRadius: "20px",
    backgroundColor: "var(--color-primary)",
  },

  selectedProjectPill__name: {
    fontSize: "16px",
    margin: "0",
    color: "white",
    maxWidth: "calc(100% - 20px)",

    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  pillButton: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "50%",
    padding: "4px",
    color: "white",

    "&:hover": {
      cursor: "pointer",
      color: "#D84949",
    },
  },

  pillButton__icon: {
    width: "16px",
    height: "16px",

    display: "block",
  },

  projectSelect: {
    height: "28px",
    maxWidth: "100%",

    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    fontWeight: "normal",

    border: "none",
    borderRadius: "4px",

    color: "rgba(12, 14, 41, 0.4)",
    backgroundColor: "#F8F8F8",

    padding: "0px 8px",

    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#EDEDED",
    },

    "&:focus-visible": {
      border: "none",
      outline: "none",
    },
  },

  projectSelect__option: {
    padding: "8px 12px",
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    fontWeight: "normal",
    backgroundColor: "#fff",
    color: "#0C0E29",

    "&:hover": {
      backgroundColor: "#EDEDED",
    },

    "&[disabled]": {
      color: "rgba(12, 14, 41, 0.3)",
      backgroundColor: "#F8F8F8",
    },
  },

  listDescriptionStyle: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    position: "relative",
  },

  description__textarea: {
    height: "100%",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    backgroundColor: "transparent",
    border: "1px solid #BDBDBD",
    borderRadius: "4px",
    resize: "none",

    [theme.breakpoints.down("sm")]: {
      height: "120px",
    },
  },

  projectOption__labelContainer: {
    display: "flex",
    gap: "8px",
  },

  labelContainer__label: {
    fontSize: "16px",
    color: "rgb(105, 105, 105)",
    margin: "0",
  },

  labelContainer__helperText: {
    fontSize: "16px",
    color: "#D84949",
    margin: "0",
  },

  graphControlsRow: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "8px 0",
    "& > :last-child": { flex: 1, minWidth: 0 },
  },

  chartContainer: {
    width: "100%",
    position: "relative",
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "24px",
    paddingBottom: "0px",
    marginBottom: "16px",
    boxSizing: "border-box",
  },

  yamazumiContainer: {
    position: "relative",
    minHeight: "400px",
    marginBottom: "16px",
  },

  emptyStateContainer: {
    width: "100%",
    height: "100%",

    position: "absolute",
    left: "0px",
    top: "0px",
    zIndex: "998",

    backgroundColor: "rgba(237, 237, 237, 0.48)",
    backdropFilter: "blur(8px)", // Apply blur to background elements

    borderRadius: "8px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  emptyStateCard: {
    maxWidth: "320px",
    boxSizing: "border-box",

    padding: "16px",

    display: "flex",
    flexDirection: "column",

    alignItems: "center",

    backgroundColor: "white",

    border: "1px solid #EDEDED",
    borderRadius: "8px",
  },

  emptyStateCard__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    color: "rgba(12, 14, 41, 1)",

    margin: "0px",
    marginBottom: "8px",
  },

  emptyStateCard__subtitle: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    color: "rgba(12, 14, 41, 0.8)",

    textAlign: "center",

    margin: "0px",
  },

  chartContainer__buttonContainer: {
    width: "100%",

    display: "flex",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap",

    marginBottom: "8px",
  },

  buttonContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  },

  // Filter on graph ---------------------------------------------------------------------------------------

  filterBlock: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
    [theme.breakpoints.up("sm")]: {
      flexWrap: "nowrap",
      justifyContent: "flex-start",
      gap: "8px",
    },
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      justifyContent: "space-evenly",
    },
  },

  // Data table ------------------------------------------------------------------------------

  container: {
    boxSizing: "border-box",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    borderRadius: "8px",
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
  },

  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",

    gap: "1rem",

    [theme.breakpoints.down("sm")]: {
      gap: "0.5rem",
    },
  },

  sharingContainer: {
    position: "relative",
  },

  secondaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",
    border: "1px solid #a77d4f",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#a77d4f",
    backgroundColor: "transparent",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      border: "1px solid #a77d4f",
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  primaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",
    strokeWidth: "2px",

    display: "block",
  },

  saveButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    whiteSpace: "nowrap",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },

    // mobile
    [theme.breakpoints.down("sm")]: {
      height: "2rem",

      "&:hover": {},
    },
  },

  sharingButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "1px solid #a77d4f",
    borderRadius: "4px",

    fontSize: "16px",
    color: "#a77d4f",
    backgroundColor: "transparent",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      border: "1px solid #a77d4f",
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },

    // mobile
    [theme.breakpoints.down("sm")]: {
      height: "2rem",
      width: "2rem",

      padding: ".375rem",

      border: "none",
      backgroundColor: "#DCDCDC",
      color: "#0C0E29",

      "&:hover": {
        backgroundColor: "#BDBDBD",
        color: "#0C0E29",
        border: "none",
      },
    },
  },

  sharingButton__text: {
    // mobile
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  sharingButton__icon: {
    height: "20px",
    width: "20px",
    strokeWidth: "2px",

    display: "block",
  },

  sharingDialog: {
    width: "320px",
    maxWidth: "90vw",
    height: "fit-content",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "24px",
    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    position: "absolute",
    zIndex: "998",
    top: "48px",
    right: "0px",
    padding: "16px",

    [theme.breakpoints.down("sm")]: {
      left: "50%",
      transform: "translateX(-50%)",
    },
  },

  sharingDialog__disclaimer: {
    textAlign: "center",
    color: "rgba(12, 14, 41, 0.6)",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
  },

  disclaimer__highlight: {
    color: "#F69127",
  },

  sharingDialog__buttonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  sharingDialog__switchContainer: {
    width: "fit-content",
    height: "40px",

    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "8px",
  },

  switchContainer__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    gap: "8px !important",
    margin: "0px !important",

    userSelect: "none",
  },

  sharingDialog__primaryButton: {
    height: "32px",
    padding: "0 12px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    position: "relative",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  copied: {
    opacity: 0,
  },

  copiedText: {
    opacity: 1,
    color: "white",
    position: "absolute",
  },

  primaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },
  },

  secondaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "1px solid var(--color-primary)",
    borderRadius: "4px",

    fontSize: "16px",
    color: "var(--color-primary)",
    backgroundColor: "transparent",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      border: "1px solid #a77d4f",
      cursor: "pointer",
    },
  },

  displayLabel: {
    height: "40px",
    marginRight: "10px !important",
    border: "1px solid var(--color-primary) !important",
    textTransform: "capitalize !important",
    color: "var(--color-primary) !important",
    fontSize: "16px !important",
    fontFamily: "inherit !important",
    fontWeight: "normal !important",
    "&.Mui-selected": {
      color: "white !important",
      backgroundColor: "var(--color-primary) !important",
    },
  },

  // FINANCIAL - STYLING
  financialDetailInformationContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: "16px",

    marginBottom: "16px",

    [theme.breakpoints.down("md")]: {
      flexDirection: "column",
    },
  },

  highLowContainer: {
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    flexBasis: "0",
    flexGrow: "1",

    background: "white",
    borderRadius: "8px",

    padding: "16px",

    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },

  highLow__projectContainer: {},

  highLow__projectSubtitle: {
    fontSize: "18px",
    margin: "0px",
  },

  highLow__detail: {
    display: "flex",
    flexDirection: "column",
  },

  highLow__headerContainer: {
    width: "100%",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    flexDirection: "row",
    backgroundColor: "#ede6df",
  },

  highLow__detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",

    fontSize: "16px",
    fontWeight: "600",

    padding: "10px",
  },

  highLow__detailTitle: {
    margin: "0",

    whiteSpace: "nowrap",
  },

  headerContainer__detailInformation: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  detailInformation__registrationPoint: {
    display: "flex",
    alignItems: "center",
    gap: "4px",

    // overflow ...
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    fontSize: "14px",
  },

  registrationPoint__icon: {
    height: "20px",
    width: "20px",

    strokeWidth: "1.4px",
  },

  financialDetailInformationContainer__right: {
    display: "flex",
    flexDirection: "column",
    flexBasis: "0",
    flexGrow: "2",
    gap: "16px",
  },

  pieChartsContainer: {
    width: "100%",

    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: "16px",

    overflow: "hidden",

    borderRadius: "8px",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  pieStyle: {
    width: "50%",
    boxSizing: "border-box",

    position: "relative",

    backgroundColor: "white",

    borderRadius: "8px",

    padding: "16px",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },

  financialSummaryContainer: {
    overflow: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px",
    gap: "10px",
  },

  filterTitle: {
    backgroundColor: "var(--color-primary) !important",
    color: "white",
    fontSize: "18px",
    fontWeight: "500",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    alignItems: "center",
  },

  icon: {
    color: "#0C0E29",
    display: "block",

    height: "24px",
    width: "24px",

    strokeWidth: "1.8px",
  },
}));